import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Building2, CalendarDays, ChevronDown, Filter, MoreHorizontal, Plus, Search, ShieldAlert, Trash2, Users, X } from 'lucide-react';
import Lottie from 'lottie-react';
import styled from 'styled-components';
import BaseCard from '../../../../components/Card';
import discordAnimation from '../../../../assets/discord.json';
import PortalAvatar from '../../../../components/profile/PortalAvatar';
import { useAuth } from '../../../../context/AuthProvider';
import { apiFetch } from '../../../../lib/api';
import { INDUSTRY_OPTIONS } from '../../../../lib/industryOptions';
import OrganizationAIAgentPanel from './OrganizationAIAgentPanel';
import OrganizationServicesPanel from './OrganizationServicesPanel';
import {
  SCHEDULE_MEETING_TYPES,
  SCHEDULE_MEETING_TYPE_LABELS,
  type ScheduleMeetingType,
} from '../../../../lib/schedule/meetingTypes';

type OrganizationRecord = {
  id: string;
  name: string;
  organizationType: 'business' | 'individual';
  businessName: string | null;
  memberCount: number;
  ownerDisplay: string;
  ownerEmail: string;
  logoURL: string | null;
  assignedServiceTeam: string | null;
  primaryServiceActive: boolean | null;
  primarySubscriptionStatus: string | null;
  isSuspended: boolean;
};
type OrganizationMember = {
  uid: string;
  userId: string;
  displayName: string;
  email: string | null;
  photoURL: string | null;
  contactPhone: string | null;
  role: string;
  isPrimaryAccountHolder: boolean;
};
type OrganizationAccountHealth = {
  activeServiceCount: number;
  contractedMonthlyMinutesMin: number | null;
  contractedMonthlyMinutesMax: number | null;
  consumedBillableMinutes: number;
  liveActiveMinutes: number;
  blockedBillableMinutes: number;
  plannedRemainingEstimateMinutes: number;
  totalEstimatedMinutes: number;
  remainingBillableMinutesMin: number | null;
  remainingBillableMinutesMax: number | null;
  withinTargetCount: number;
  outsideTargetCount: number;
  trackedTicketCount: number;
  usagePeriodStartAt: string | null;
  usagePeriodEndAt: string | null;
  usageMeasuredThroughAt: string | null;
};
type OrganizationServiceContract = {
  id: string;
  status: string;
  primaryTeamLabels: string[];
  serviceCode: string | null;
  serviceName: string | null;
  tierName: string | null;
  selectedServiceOfferings: string[];
  selectedServiceOfferingCodes: string[];
  offeringEntitlements: Array<{ offeringCode: string; offeringName: string }>;
  addOnNames: string[];
  contractTerm: string | null;
  slaPolicies: Array<{ name: string; responseTargetMinutes: number | null; resolutionTargetMinutes: number | null; targetCompletionMinutes: number | null }>;
  contractedMonthlyMinutesMin: number | null;
  contractedMonthlyMinutesMax: number | null;
  consumedBillableMinutes: number;
  liveActiveMinutes: number;
  blockedBillableMinutes: number;
  plannedRemainingEstimateMinutes: number;
  totalEstimatedMinutes: number;
  remainingBillableMinutesMin: number | null;
  remainingBillableMinutesMax: number | null;
  withinTargetCount: number;
  outsideTargetCount: number;
  trackedTicketCount: number;
  usagePeriodStartAt: string | null;
  usagePeriodEndAt: string | null;
  usageMeasuredThroughAt: string | null;
};
type OrganizationScheduledMeeting = {
  id: string;
  orgId: string | null;
  serviceContractId: string | null;
  serviceCode: string | null;
  serviceName: string | null;
  hostUserId?: string | null;
  organizerUserId?: string | null;
  clientUserId?: string | null;
  slotId?: string | null;
  meetingType: ScheduleMeetingType;
  title: string;
  description?: string | null;
  meetingLinks: string[];
  discordMeeting?: {
    scheduledEventId: string;
    scheduledEventUrl: string;
    textChannelId: string;
    textChannelUrl: string;
    voiceChannelId: string;
    voiceChannelName: string;
    voiceChannelUrl: string;
    messageId: string | null;
    messageUrl: string | null;
    syncedAt: string;
  } | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'requested' | 'scheduled' | 'canceled' | 'completed' | 'no_show';
  requestStatus?: 'pending' | 'confirmed' | null;
  requestedForDate?: string | null;
  requestedAt?: string | null;
  attendees: Array<{ id: string; userId: string | null; email: string | null; name: string | null; role: string | null }>;
  createdAt?: string;
  updatedAt?: string;
};
type OrganizationMeetingNote = {
  id: string;
  eventId: string;
  orgId: string | null;
  serviceContractId: string | null;
  serviceCode: string | null;
  serviceName: string | null;
  summary: string | null;
  clientUpdates: string | null;
  decisions: string | null;
  nextSteps: string | null;
  internalContext: string | null;
  onboardingProfile?: OrganizationMeetingOnboardingProfile | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
};
type OrganizationMeetingOnboardingProfile = {
  businessOverview: string | null;
  clientGoals: string | null;
  successMetrics: string | null;
  stakeholders: string | null;
  servicesInScope: string | null;
  immediatePriorities: string | null;
  firstMilestoneDeliverables: string | null;
  projectOrEngagementType: string | null;
  targetUsersAndRoles: string | null;
  coreWorkflowsJourneys: string | null;
  geographyMarketScope: string | null;
  blockingClarifications: string | null;
  clarificationOwners: string | null;
  proceedabilityAssessment: string | null;
  recommendedDiscoveryOutcome: string | null;
  constraintsDependencies: string | null;
  accessAssets: string | null;
  communicationPreferences: string | null;
  risksSensitivities: string | null;
  launchDate: string | null;
  firstMilestoneTargetDate: string | null;
  primaryContact: string | null;
  primaryApprover: string | null;
  currentState: string | null;
  pastAttempts: string | null;
  priorityRanking: string | null;
  outOfScope: string | null;
  clientDependencies: string | null;
  approvalTurnaround: string | null;
  platformTargets: string | null;
  externalSystemsIntegrations: string | null;
  trustComplianceRequirements: string | null;
  monetizationCommercialModel: string | null;
  brandStateGuidelines: string | null;
  designAssetsReadiness: string | null;
  marketingChannelsCadence: string | null;
  campaignOffersPromotions: string | null;
  paidMediaBudgetSignals: string | null;
};
type OrganizationMeetingNoteDraft = {
  summary: string;
  clientUpdates: string;
  decisions: string;
  nextSteps: string;
  internalContext: string;
  onboardingProfile: Record<keyof OrganizationMeetingOnboardingProfile, string>;
};
type OrganizationMeetingEventDraft = {
  hostUserUid: string;
  meetingType: ScheduleMeetingType | '';
  startsAtLocal: string;
  durationMinutes: number;
  title: string;
  description: string;
  serviceContractId: string;
  meetingLinks: string[];
  createDiscordMeeting: boolean;
};
type InternalScheduleUser = {
  userUid: string;
  name: string;
  email: string | null;
  photoURL: string | null;
  role: 'admin' | 'agent';
};
type OrganizationMeetingDraft = {
  hostUserUid: string;
  meetingType: ScheduleMeetingType | '';
  serviceContractId: string;
  startsAtLocal: string;
  durationMinutes: number;
  title: string;
  description: string;
  meetingLinks: string[];
  createDiscordMeeting: boolean;
  orgMemberUserIds: string[];
  internalAttendeeUids: string[];
};
type OrganizationDetails = {
  id: string;
  name: string;
  organizationType: 'business' | 'individual';
  businessName: string | null;
  industry: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  address: string | null;
  logoURL: string | null;
  ownerDisplay: string;
  ownerEmail: string | null;
  ownerContactPhone: string | null;
  memberCount: number;
  pendingInviteCount: number;
  projectCount: number;
  ticketCount: number;
  assignedServiceTeam: string | null;
  assignedServiceTeams: string[];
  primarySubscriptionStatus: string | null;
  primaryServiceActive: boolean | null;
  isSuspended: boolean;
  suspendedAt: string | null;
  suspendedByUserId: string | null;
  purchasedServices: string[];
  accountHealth: OrganizationAccountHealth | null;
  serviceContracts: OrganizationServiceContract[];
  members: OrganizationMember[];
  primaryAccountHolder: OrganizationMember | null;
};
type WorkspaceTab = 'overview' | 'meetings' | 'members' | 'services' | 'ai_agent';
type OrganizationServiceTabCode = 'marketing' | 'platform' | 'assets_design';
type ServiceStateFilter = 'all' | 'active' | 'inactive';
type TeamFilter = 'all' | 'with_team' | 'without_team';
type MeetingStatusFilter = 'all' | 'requested' | 'scheduled' | 'completed' | 'canceled' | 'no_show';
type MeetingSortOrder = 'newest' | 'oldest' | 'soonest';
type MeetingServiceFilter = 'all' | 'unassigned' | string;
type MeetingTypeFilter = 'all' | ScheduleMeetingType;
type CreateOrganizationDraft = {
  businessName: string;
  industry: string;
  contactPhone: string;
  website: string;
  address: string;
};
type InvitePlatformClient = {
  uid: string;
  email: string;
  name: string | null;
  photoURL: string | null;
  businessName: string | null;
};
type MemberInviteRole = 'member' | 'owner';
type MeetingModalState =
  | null
  | { mode: 'create'; orgId: string; orgName: string }
  | { mode: 'view'; orgId: string; orgName: string; meetingId: string };

const ORGANIZATION_SCHEDULE_MEETING_TYPES = SCHEDULE_MEETING_TYPES.filter(
  (value) => value !== 'interview' && value !== 'vacation' && value !== 'out_of_office',
);

const ORGANIZATION_SERVICE_TABS: Array<{ code: OrganizationServiceTabCode; label: string }> = [
  { code: 'marketing', label: 'Managed Marketing' },
  { code: 'platform', label: 'Platform Development' },
  { code: 'assets_design', label: 'Assets/Design' },
];

const getOrganizationServiceTabLabel = (serviceTab: OrganizationServiceTabCode) => (
  ORGANIZATION_SERVICE_TABS.find((entry) => entry.code === serviceTab)?.label ?? 'Service'
);

const Page = styled.section`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
  min-height: calc(100dvh - 2.5rem);
  height: calc(100dvh - 2.5rem);
`;
const Header = styled(BaseCard)`padding:12px;border:none;background:transparent;box-shadow:none;display:grid;gap:4px;`;
const Workspace = styled.div`
  display:grid;gap:12px;min-height:0;height:100%;grid-template-columns:clamp(280px,24vw,360px) minmax(0,1fr);
  @media (max-width:1180px){grid-template-columns:1fr;height:auto;}
`;
const ServiceContractSnapshotGrid = styled.div`
  display:grid;
  gap:12px;
  grid-template-columns:repeat(3, minmax(0,1fr));

  @media (max-width:1180px){
    grid-template-columns:repeat(2, minmax(0,1fr));
  }

  @media (max-width:760px){
    grid-template-columns:minmax(0,1fr);
  }
`;
const ServiceContractOfferingsList = styled.div`
  display:grid;
  gap:8px 16px;
  grid-template-columns:repeat(2, minmax(0,1fr));

  @media (max-width:760px){
    grid-template-columns:minmax(0,1fr);
  }
`;
const Panel = styled(BaseCard)`padding:0;min-height:0;height:100%;overflow:hidden;`;
const SmallButton = styled.button<{ $accent?: boolean }>`
  min-height:38px;padding:0 12px;border-radius:10px;border:1px solid ${({ $accent }) => ($accent ? 'rgba(224,161,94,.45)' : 'rgba(255,255,255,.14)')};
  background:${({ $accent }) => ($accent ? 'rgba(224,161,94,.14)' : 'rgba(255,255,255,.04)')};color:#e8eaed;font-size:12px;font-weight:800;letter-spacing:.2px;text-transform:uppercase;cursor:pointer;
  &:disabled{opacity:.42;cursor:not-allowed;}
`;
const HeaderActionsWrap = styled.div`
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  align-items:center;
  align-self:end;
  margin-bottom:-10px;
`;
const OverflowMenuAnchor = styled.div`
  position:relative;
`;
const OverflowIconButton = styled.button`
  width:36px;
  min-width:36px;
  height:36px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border-radius:10px 10px 0 0;
  border:1px solid rgba(255,255,255,.08);
  border-bottom-color:transparent;
  background:rgba(255,255,255,.02);
  color:#e8eaed;
  cursor:pointer;

  &:focus-visible{
    outline:none;
    border-color:rgba(224,161,94,.45);
    box-shadow:0 0 0 1px rgba(224,161,94,.2);
  }
`;
const OverflowMenu = styled.div`
  position:absolute;
  top:calc(100% + 10px);
  right:0;
  width:220px;
  z-index:20;
  border:1px solid rgba(255,255,255,.14);
  border-radius:14px;
  background:#171b23;
  box-shadow:0 24px 60px rgba(0,0,0,.45);
  padding:8px;
  display:grid;
  gap:6px;
`;
const OverflowMenuItem = styled.button<{ $danger?: boolean }>`
  width:100%;
  min-height:38px;
  padding:0 12px;
  border-radius:10px;
  border:1px solid ${({ $danger }) => ($danger ? 'rgba(255,110,110,.18)' : 'rgba(255,255,255,.08)')};
  background:${({ $danger }) => ($danger ? 'rgba(255,110,110,.08)' : 'rgba(255,255,255,.03)')};
  color:${({ $danger }) => ($danger ? '#ffd2d2' : '#f3f4f6')};
  display:flex;
  align-items:center;
  gap:10px;
  font-size:12px;
  font-weight:800;
  cursor:pointer;

  &:disabled{
    opacity:.45;
    cursor:not-allowed;
  }
`;
const CreateOrganizationSelectWrap = styled.div`
  position:relative;
`;
const MemberInviteSearchWrap = styled.div`
  position:relative;
`;
const CreateOrganizationSelectButton = styled.button<{ $empty?: boolean }>`
  width:100%;
  min-height:40px;
  border-radius:10px;
  border:1px solid rgba(255,255,255,.12);
  background:rgba(255,255,255,.03);
  color:${({ $empty }) => ($empty ? '#9aa4b2' : '#f3f4f6')};
  padding:0 12px;
  font-size:13px;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  cursor:pointer;

  &:disabled{
    opacity:.42;
    cursor:not-allowed;
  }

  &:focus-visible{
    outline:none;
    border-color:rgba(224,161,94,.7);
    box-shadow:0 0 0 2px rgba(224,161,94,.15);
  }
`;
const CreateOrganizationSelectMenu = styled.div`
  position:absolute;
  top:calc(100% + 8px);
  left:0;
  right:0;
  z-index:30;
  border:1px solid rgba(255,255,255,.14);
  border-radius:14px;
  background:#171b23;
  box-shadow:0 24px 60px rgba(0,0,0,.45);
  padding:8px;
  display:grid;
  gap:6px;
  max-height:240px;
  overflow-y:auto;
`;
const CreateOrganizationSelectOption = styled.button<{ $active?: boolean }>`
  width:100%;
  min-height:34px;
  padding:0 10px;
  border-radius:10px;
  border:1px solid ${({ $active }) => ($active ? 'rgba(224,161,94,.3)' : 'transparent')};
  background:${({ $active }) => ($active ? 'rgba(224,161,94,.12)' : 'transparent')};
  color:#f3f4f6;
  font-size:12px;
  font-weight:700;
  text-align:left;
  cursor:pointer;

  &:hover{
    background:rgba(255,255,255,.06);
  }
`;
const MemberInviteSelect = styled.select`
  width:100%;
  min-height:40px;
  border-radius:10px;
  border:1px solid rgba(255,255,255,.12);
  background:rgba(255,255,255,.03);
  color:#f3f4f6;
  padding:0 12px;
  font-size:13px;
  font-weight:700;

  &:focus{
    outline:none;
    border-color:rgba(224,161,94,.7);
    box-shadow:0 0 0 2px rgba(224,161,94,.15);
  }

  &:disabled{
    opacity:.42;
    cursor:not-allowed;
  }
`;
const SearchInput = styled.input`
  width:100%;min-height:38px;border-radius:10px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:#e8eaed;padding:0 14px 0 40px;font-size:13px;font-weight:700;
  &::placeholder{color:#9aa4b2;} &:focus{outline:none;border-color:rgba(224,161,94,.7);box-shadow:0 0 0 2px rgba(224,161,94,.15);}
`;
const EmptyState = styled.div`border:1px dashed rgba(255,255,255,.18);border-radius:16px;padding:18px;color:rgba(255,255,255,.66);text-align:center;font-size:13px;font-weight:700;`;
const StatusPill = styled.span<{ $tone?: 'neutral' | 'good' | 'warn' | 'bad' }>`
  min-height:24px;padding:0 8px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:900;letter-spacing:.26px;text-transform:uppercase;
  border:1px solid ${({ $tone = 'neutral' }) => $tone === 'good' ? 'rgba(72,199,142,.35)' : $tone === 'warn' ? 'rgba(224,161,94,.35)' : $tone === 'bad' ? 'rgba(255,110,110,.35)' : 'rgba(255,255,255,.14)'};
  background:${({ $tone = 'neutral' }) => $tone === 'good' ? 'rgba(72,199,142,.12)' : $tone === 'warn' ? 'rgba(224,161,94,.12)' : $tone === 'bad' ? 'rgba(255,110,110,.12)' : 'rgba(255,255,255,.04)'};color:#f3f4f6;
`;
const ModalOverlay = styled.div`position:fixed;inset:0;z-index:70;background:rgba(7,10,14,.72);overflow-y:auto;overflow-x:hidden;padding:24px;`;
const MeetingModalOverlay = styled(ModalOverlay)`
  display:flex;
  align-items:center;
  justify-content:center;
  overflow:hidden;
`;
const ModalCard = styled(BaseCard)`width:min(520px,100%);padding:16px;display:grid;gap:14px;overflow:visible;`;
const MeetingModalCard = styled(BaseCard)`
  width:min(1080px,100%);
  max-height:min(calc(100vh - 48px), 1120px);
  margin:0 auto;
  padding:0;
  display:grid;
  grid-template-rows:auto minmax(0,1fr) auto;
  overflow:hidden;
`;
const MeetingModalBody = styled.div`
  min-height:0;
  overflow-y:auto;
  overflow-x:hidden;
  padding:16px;
  padding-bottom:20px;
  display:flex;
  flex-direction:column;
  gap:16px;
  scrollbar-width:thin;
  scrollbar-color:rgba(224,161,94,.36) rgba(255,255,255,.04);
  &::-webkit-scrollbar{width:10px;}
  &::-webkit-scrollbar-track{background:rgba(255,255,255,.04);border-radius:999px;}
  &::-webkit-scrollbar-thumb{
    background:linear-gradient(180deg, rgba(224,161,94,.5), rgba(224,161,94,.28));
    border-radius:999px;
    border:2px solid rgba(24,27,34,.94);
  }
  &::-webkit-scrollbar-thumb:hover{
    background:linear-gradient(180deg, rgba(224,161,94,.64), rgba(224,161,94,.38));
  }
`;
const MeetingFlatSection = styled.div`display:grid;gap:10px;min-width:0;align-content:start;`;
const MeetingFlatRule = styled.div`height:1px;background:rgba(255,255,255,.08);`;
const MeetingSection = styled.div`
  display:grid;
  gap:10px;
  min-width:0;
  padding:0;
  background:transparent;
  border:none;
  border-radius:0;
  box-shadow:none;
  overflow:visible;
`;
const MeetingEditorSection = styled.div`
  display:grid;
  gap:16px;
  min-width:0;
  padding:0;
  background:transparent;
  border:none;
  border-radius:0;
  box-shadow:none;
  overflow:visible;
`;
const MeetingEditorRow = styled.div`
  display:grid;
  gap:8px;
  padding:0;
  min-width:0;
`;
const MeetingEditorRowHeader = styled.div`display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;`;
const MeetingEditorRowActions = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-end;`;
const MeetingNotesSection = styled.div`display:grid;gap:12px;min-width:0;`;
const MeetingGrid = styled.div<{ $cols?: string }>`display:grid;gap:12px;grid-template-columns:${({ $cols }) => $cols || 'repeat(2, minmax(0,1fr))'};@media (max-width:900px){grid-template-columns:1fr;}`;
const MeetingField = styled.label`display:grid;gap:6px;min-width:0;`;
const MeetingFieldLabel = styled.span`color:#9aa4b2;font-size:10px;font-weight:900;letter-spacing:.24px;text-transform:uppercase;`;
const MeetingInput = styled.input`
  width:100%;min-height:40px;border-radius:10px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.03);color:#f3f4f6;padding:0 12px;font-size:13px;font-weight:700;box-sizing:border-box;display:block;max-width:100%;
  &::placeholder{color:#8c97a7;} &:focus{outline:none;border-color:rgba(224,161,94,.7);box-shadow:0 0 0 2px rgba(224,161,94,.14);}
`;
const MeetingSelect = styled.select`
  width:100%;min-height:40px;border-radius:10px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.03);color:#f3f4f6;padding:0 12px;font-size:13px;font-weight:700;box-sizing:border-box;display:block;max-width:100%;
  &:focus{outline:none;border-color:rgba(224,161,94,.7);box-shadow:0 0 0 2px rgba(224,161,94,.14);}
`;
const MeetingTextArea = styled.textarea`
  width:100%;min-height:120px;margin:0;border-radius:10px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.03);color:#f3f4f6;padding:12px;font-size:13px;font-weight:700;line-height:1.6;resize:none;overflow-y:auto;overflow-x:hidden;box-sizing:border-box;display:block;max-width:100%;align-self:start;
  scrollbar-width:thin;
  scrollbar-color:rgba(224,161,94,.42) rgba(255,255,255,.05);
  &::-webkit-scrollbar{width:10px;}
  &::-webkit-scrollbar-track{background:rgba(255,255,255,.05);border-radius:999px;}
  &::-webkit-scrollbar-thumb{
    background:linear-gradient(180deg, rgba(224,161,94,.58), rgba(224,161,94,.34));
    border-radius:999px;
    border:2px solid rgba(24,27,34,.94);
  }
  &::-webkit-scrollbar-thumb:hover{
    background:linear-gradient(180deg, rgba(224,161,94,.72), rgba(224,161,94,.46));
  }
  &::placeholder{color:#8c97a7;} &:focus{outline:none;border-color:rgba(224,161,94,.72);box-shadow:inset 0 0 0 1px rgba(224,161,94,.18);}
`;
const MeetingNotesTextArea = styled(MeetingTextArea)`
  min-height:120px;
`;
const MeetingEditorInput = styled.input`
  width:100%;
  min-width:0;
  border:none;
  background:transparent;
  color:#f3f4f6;
  padding:0;
  margin:0;
  font-size:15px;
  font-weight:800;
  line-height:1.4;
  box-sizing:border-box;
  &::placeholder{color:#8c97a7;}
  &:focus{outline:none;}
`;
const MeetingEditorTextArea = styled.textarea`
  width:100%;
  min-width:0;
  min-height:88px;
  max-height:180px;
  border:none;
  background:transparent;
  color:#f3f4f6;
  padding:0;
  margin:0;
  font-size:13px;
  font-weight:700;
  line-height:1.6;
  resize:none;
  overflow-y:auto;
  overflow-x:hidden;
  box-sizing:border-box;
  scrollbar-width:thin;
  scrollbar-color:rgba(224,161,94,.36) rgba(255,255,255,.04);
  &::-webkit-scrollbar{width:10px;}
  &::-webkit-scrollbar-track{background:rgba(255,255,255,.04);border-radius:999px;}
  &::-webkit-scrollbar-thumb{
    background:linear-gradient(180deg, rgba(224,161,94,.5), rgba(224,161,94,.28));
    border-radius:999px;
    border:2px solid rgba(24,27,34,.94);
  }
  &::-webkit-scrollbar-thumb:hover{
    background:linear-gradient(180deg, rgba(224,161,94,.64), rgba(224,161,94,.38));
  }
  &::placeholder{color:#8c97a7;}
  &:focus{outline:none;}
`;
const MeetingHelper = styled.div<{ $error?: boolean }>`color:${({ $error }) => ($error ? '#fca5a5' : '#9aa4b2')};font-size:11px;line-height:1.5;font-weight:700;`;
const MeetingExternalLinkList = styled.div`display:grid;gap:10px;`;
const MeetingExternalLinkRow = styled.div`display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:center;`;
const MeetingExternalLinkPreviewList = styled.div`display:flex;flex-wrap:wrap;gap:8px;`;
const MeetingExternalLink = styled.a`
  min-height:32px;padding:0 12px;border-radius:999px;border:1px solid rgba(224,161,94,.24);background:rgba(224,161,94,.08);color:#f3f4f6;
  font-size:11px;font-weight:800;display:inline-flex;align-items:center;gap:8px;text-decoration:none;
  &:hover{border-color:rgba(224,161,94,.42);background:rgba(224,161,94,.14);}
`;
const MeetingDiscordSummaryRow = styled.div`
  display:grid;
  grid-template-columns:auto minmax(0,1fr) auto;
  gap:12px;
  align-items:center;
  min-width:0;
  padding:10px 12px;
  border:1px solid rgba(88, 101, 242, .28);
  border-radius:12px;
  background:rgba(88, 101, 242, .08);
  color:#f3f4f6;
  text-decoration:none;
  &:hover{
    border-color:rgba(88, 101, 242, .42);
    background:rgba(88, 101, 242, .12);
  }
`;
const MeetingDiscordSummaryLink = styled.a`
  min-width:0;
  display:grid;
  gap:2px;
  color:inherit;
  text-decoration:none;
`;
const MeetingDiscordSummaryAnim = styled.div`
  width:32px;
  height:32px;
  flex:0 0 32px;
  pointer-events:none;
`;
const MeetingDiscordRemoveButton = styled.button`
  width:32px;
  height:32px;
  min-width:32px;
  padding:0;
  border-radius:10px;
  border:1px solid rgba(255,255,255,.14);
  background:rgba(255,255,255,.04);
  color:#f3f4f6;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  transition:border-color 140ms ease, background 140ms ease, color 140ms ease;
  &:hover:not(:disabled){
    border-color:rgba(248, 113, 113, .48);
    background:rgba(166, 76, 68, .14);
    color:#fecaca;
  }
  &:disabled{
    opacity:.45;
    cursor:not-allowed;
  }
`;
const MeetingDiscordActionButton = styled.button<{ $active?: boolean }>`
  width:42px;
  height:42px;
  min-width:42px;
  padding:0;
  border-radius:12px;
  border:1px solid ${({ $active }) => $active ? 'rgba(88, 101, 242, .58)' : 'rgba(88, 101, 242, .34)'};
  background:${({ $active }) => $active ? 'rgba(88, 101, 242, .22)' : 'rgba(88, 101, 242, .12)'};
  color:#f3f4f6;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  transition:border-color 140ms ease, background 140ms ease, transform 140ms ease;
  &:hover:not(:disabled){
    border-color:rgba(88, 101, 242, .54);
    background:rgba(88, 101, 242, .18);
    transform:translateY(-1px);
  }
  &:disabled{
    opacity:.5;
    cursor:not-allowed;
    transform:none;
  }
`;
const MeetingDiscordButtonAnim = styled.div`
  width:36px;
  height:36px;
  flex:0 0 36px;
  pointer-events:none;
`;
const MeetingCardButton = styled.button`
  width:100%;text-align:left;border-radius:12px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02);color:#e8eaed;padding:12px;display:grid;grid-template-columns:minmax(0,1fr) auto;column-gap:12px;row-gap:8px;align-items:start;cursor:pointer;
  &:hover{border-color:rgba(224,161,94,.28);background:rgba(224,161,94,.06);}
  &:focus-visible{outline:none;border-color:rgba(224,161,94,.45);box-shadow:0 0 0 2px rgba(224,161,94,.16);}
`;
const MeetingCardContent = styled.div`display:grid;gap:4px;min-width:0;`;
const MeetingCardActions = styled.div`display:flex;align-items:center;justify-content:flex-end;gap:8px;align-self:start;`;
const MeetingCardTitle = styled.div`
  min-width:0;
  color:#f3f4f6;
  font-size:14px;
  font-weight:900;
  line-height:1.35;
`;
const MeetingCardMetaLine = styled.div<{ $tone?: 'default' | 'warn' }>`
  min-width:0;
  color:${({ $tone = 'default' }) => ($tone === 'warn' ? '#e0a15e' : '#c7cfdb')};
  font-size:11px;
  font-weight:800;
  line-height:1.4;
`;
const MeetingCardStatus = styled.span<{ $tone?: 'neutral' | 'good' | 'warn' | 'bad' }>`
  color:${({ $tone = 'neutral' }) => $tone === 'good' ? '#7dd3a6' : $tone === 'warn' ? '#e0a15e' : $tone === 'bad' ? '#fca5a5' : '#c7cfdb'};
  font-size:10px;
  font-weight:900;
  letter-spacing:.28px;
  line-height:1.2;
  text-transform:uppercase;
`;
const MeetingCardDetail = styled.div`
  min-width:0;
  color:#9aa4b2;
  font-size:11px;
  font-weight:700;
  line-height:1.5;
`;
const MeetingDeleteIconButton = styled.button`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(255,255,255,.04);
  color: #f3f4f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease, color 140ms ease;

  &:hover:not(:disabled) {
    border-color: rgba(248, 113, 113, .52);
    background: rgba(166, 76, 68, .14);
    color: #fecaca;
  }

  &:disabled {
    opacity: .42;
    cursor: not-allowed;
  }
`;
const MeetingsWorkspaceCard = styled(BaseCard)`
  padding: 0;
  display: grid;
  gap: 12px;
  min-height: 0;
  height: 100%;
  grid-template-rows: auto minmax(0, 1fr);
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
`;
const MeetingsScrollArea = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  display: grid;
  gap: 10px;
  align-content: start;
`;
const MembersWorkspaceCard = styled.div`
  padding: 0;
  display: grid;
  gap: 12px;
  min-height: 0;
  height: 100%;
  grid-template-rows: auto minmax(0, 1fr);
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
`;
const MembersScrollArea = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  display: grid;
  gap: 10px;
  align-content: start;
`;
const MeetingInlineButton = styled.button`
  min-height:32px;padding:0 10px;border-radius:999px;border:1px solid #e0a15e;background:rgba(255,255,255,.03);color:#e8eaed;font-size:11px;font-weight:800;display:inline-flex;align-items:center;justify-content:center;gap:6px;cursor:pointer;
`;
const MeetingToolbar = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-start;`;
const MeetingToolbarSelect = styled.select`
  min-height:32px;padding:0 28px 0 10px;border-radius:999px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.03);color:#e8eaed;font-size:11px;font-weight:800;
  &:focus{outline:none;border-color:rgba(224,161,94,.7);box-shadow:0 0 0 2px rgba(224,161,94,.14);}
`;
const AttendeeList = styled.div`display:grid;gap:8px;align-content:start;`;
const AttendeeRow = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:10px;align-items:center;padding:10px 0;min-width:0;`;
const AttendeePicker = styled.div`display:grid;gap:8px;padding-top:8px;`;
const AttendeePickerItem = styled.button`
  width:100%;text-align:left;border-radius:10px;border:1px solid rgba(255,255,255,.08);background:transparent;color:#e8eaed;padding:10px;display:grid;grid-template-columns:auto minmax(0,1fr);gap:10px;align-items:center;cursor:pointer;
  &:hover{border-color:rgba(224,161,94,.28);background:rgba(224,161,94,.06);}
`;
const FooterButton = styled.button<{ $accent?: boolean }>`
  min-height:38px;padding:0 12px;border-radius:10px;border:1px solid ${({ $accent }) => ($accent ? 'rgba(224,161,94,.45)' : 'rgba(255,255,255,.14)')};
  background:${({ $accent }) => ($accent ? 'rgba(224,161,94,.14)' : 'rgba(255,255,255,.04)')};color:#e8eaed;font-size:12px;font-weight:800;cursor:pointer;
  &:disabled{opacity:.42;cursor:not-allowed;}
`;

const asText = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
const emailLocalPart = (email: string) => asText(email).split('@')[0] || '--';
const parseOptionalBoolean = (value: unknown) => typeof value === 'boolean' ? value : typeof value === 'string' ? (value.trim().toLowerCase() === 'true' ? true : value.trim().toLowerCase() === 'false' ? false : null) : null;
const parseOptionalNumber = (value: unknown) => value === null || value === undefined || value === '' ? null : Number.isFinite(Number(value)) ? Number(value) : null;
const deriveActiveFromStatus = (value: unknown) => ['active', 'trialing'].includes(asText(value).toLowerCase()) ? true : asText(value) ? false : null;
const formatMinutesAsHours = (value: number | null | undefined) => typeof value !== 'number' || !Number.isFinite(value) ? '--' : value >= 600 ? `${Math.round(value / 60)}h` : value >= 60 ? `${(value / 60).toFixed(1)}h` : `${Math.round(value)}m`;
const formatMinutesRange = (min: number | null | undefined, max: number | null | undefined) => typeof min === 'number' && typeof max === 'number' ? `${formatMinutesAsHours(min)} - ${formatMinutesAsHours(max)}` : typeof max === 'number' ? formatMinutesAsHours(max) : typeof min === 'number' ? formatMinutesAsHours(min) : '--';
const formatDateLabel = (value: string | null | undefined) => !value ? '--' : Number.isNaN(new Date(value).getTime()) ? '--' : new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const formatMeetingDateRange = (start: string | null | undefined, end: string | null | undefined) => !start ? '--' : `${new Date(start).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}${end ? ` - ${new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}`;
const formatRequestedMeetingDate = (value: string | null | undefined) => {
  if (!value) return '--';
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
};
const createEmptyMeetingDraft = (hostUserUid = ''): OrganizationMeetingDraft => ({
  hostUserUid,
  meetingType: 'general_meeting',
  serviceContractId: '',
  startsAtLocal: '',
  durationMinutes: 30,
  title: '',
  description: '',
  meetingLinks: [],
  createDiscordMeeting: false,
  orgMemberUserIds: [],
  internalAttendeeUids: [],
});
const EMPTY_ONBOARDING_PROFILE: Record<keyof OrganizationMeetingOnboardingProfile, string> = {
  businessOverview: '',
  clientGoals: '',
  successMetrics: '',
  stakeholders: '',
  servicesInScope: '',
  immediatePriorities: '',
  firstMilestoneDeliverables: '',
  projectOrEngagementType: '',
  targetUsersAndRoles: '',
  coreWorkflowsJourneys: '',
  geographyMarketScope: '',
  blockingClarifications: '',
  clarificationOwners: '',
  proceedabilityAssessment: '',
  recommendedDiscoveryOutcome: '',
  constraintsDependencies: '',
  accessAssets: '',
  communicationPreferences: '',
  risksSensitivities: '',
  launchDate: '',
  firstMilestoneTargetDate: '',
  primaryContact: '',
  primaryApprover: '',
  currentState: '',
  pastAttempts: '',
  priorityRanking: '',
  outOfScope: '',
  clientDependencies: '',
  approvalTurnaround: '',
  platformTargets: '',
  externalSystemsIntegrations: '',
  trustComplianceRequirements: '',
  monetizationCommercialModel: '',
  brandStateGuidelines: '',
  designAssetsReadiness: '',
  marketingChannelsCadence: '',
  campaignOffersPromotions: '',
  paidMediaBudgetSignals: '',
};
const ONBOARDING_PROFILE_FIELDS: ReadonlyArray<{
  key: keyof OrganizationMeetingOnboardingProfile;
  label: string;
  placeholder: string;
  control?: 'textarea' | 'date' | 'input';
  group:
    | 'Core Discovery'
    | 'Shared Constraints'
    | 'Platform Development'
    | 'Assets & Design'
    | 'Managed Marketing';
}> = [
  { key: 'businessOverview', label: 'Business / product overview', placeholder: 'Summarize what the client does, what they sell, and the product or service context teams need to understand.', group: 'Core Discovery' },
  { key: 'clientGoals', label: 'Client goals', placeholder: 'Capture the primary goals the client wants this engagement to achieve.', group: 'Core Discovery' },
  { key: 'successMetrics', label: 'Success metrics', placeholder: 'Document KPIs, outcomes, or success measures the client cares about.', group: 'Core Discovery' },
  { key: 'stakeholders', label: 'Stakeholders / approvers', placeholder: 'List key stakeholders, approvers, and who should be looped in for decisions.', group: 'Core Discovery' },
  { key: 'servicesInScope', label: 'Services in scope', placeholder: 'Call out the services, offerings, and workstreams currently in scope.', group: 'Core Discovery' },
  { key: 'projectOrEngagementType', label: 'Project / engagement type', placeholder: 'Example: new marketplace app, ecommerce refresh, discovery sprint, rebrand, recurring campaign support.', group: 'Core Discovery' },
  { key: 'targetUsersAndRoles', label: 'Target users / roles', placeholder: 'Describe who the primary users are and any distinct roles or audiences involved.', group: 'Core Discovery' },
  { key: 'coreWorkflowsJourneys', label: 'Core workflows / journeys', placeholder: 'Capture the critical user or business flows the client expects this work to support.', group: 'Core Discovery' },
  { key: 'geographyMarketScope', label: 'Geography / market scope', placeholder: 'Document whether the work is local, regional, national, multilingual, or market-specific.', group: 'Core Discovery' },
  { key: 'immediatePriorities', label: 'Immediate priorities', placeholder: 'Note the near-term priorities the PM agent should plan around first.', group: 'Core Discovery' },
  { key: 'currentState', label: 'Current state / baseline', placeholder: 'Describe what already exists today, what is live, and the current operational baseline.', group: 'Core Discovery' },
  { key: 'pastAttempts', label: 'What has already been tried', placeholder: 'Capture previous attempts, prior vendors, experiments, or known failed approaches.', group: 'Core Discovery' },
  { key: 'priorityRanking', label: 'Priority ranking', placeholder: 'List priorities in order so the PM agent understands what matters most when tradeoffs are required.', group: 'Core Discovery' },
  { key: 'firstMilestoneDeliverables', label: 'Expected early outcomes', placeholder: 'Capture what the client expects to learn, define, or leave the kickoff with before deeper planning begins.', group: 'Core Discovery' },
  { key: 'blockingClarifications', label: 'Blocking clarifications', placeholder: 'List the questions that must be answered before responsible planning can continue.', group: 'Core Discovery' },
  { key: 'clarificationOwners', label: 'Clarification owners', placeholder: 'Who needs to answer the missing questions: client, account manager, PM, or another stakeholder?', group: 'Core Discovery' },
  { key: 'proceedabilityAssessment', label: 'Can planning proceed without clarification?', placeholder: 'Example: discovery can proceed, implementation cannot proceed, or blocked until client responds.', group: 'Core Discovery' },
  { key: 'recommendedDiscoveryOutcome', label: 'Recommended discovery outcome', placeholder: 'If information is thin, note the likely first project outcome such as a discovery project, clarification sprint, or implementation-ready plan.', group: 'Core Discovery' },
  { key: 'constraintsDependencies', label: 'Constraints / dependencies', placeholder: 'List blockers, dependencies, approvals, timelines, or known constraints.', group: 'Shared Constraints' },
  { key: 'accessAssets', label: 'Access / assets needed', placeholder: 'Document missing access, credentials, files, data, or assets teams still need.', group: 'Shared Constraints' },
  { key: 'communicationPreferences', label: 'Communication preferences', placeholder: 'Capture cadence, channels, escalation preferences, and tone expectations.', group: 'Shared Constraints' },
  { key: 'risksSensitivities', label: 'Risks / sensitivities', placeholder: 'Record risks, reputational sensitivities, compliance issues, or client concerns teams must remember.', group: 'Shared Constraints' },
  { key: 'launchDate', label: 'Launch date / hard deadline', placeholder: '', control: 'date', group: 'Shared Constraints' },
  { key: 'firstMilestoneTargetDate', label: 'First milestone target date', placeholder: '', control: 'date', group: 'Shared Constraints' },
  { key: 'primaryContact', label: 'Primary contact', placeholder: 'Document the day-to-day client contact the team should work through.', control: 'input', group: 'Shared Constraints' },
  { key: 'primaryApprover', label: 'Primary approver', placeholder: 'Document who has final approval authority for work and milestones.', control: 'input', group: 'Shared Constraints' },
  { key: 'outOfScope', label: 'Out of scope / avoid', placeholder: 'Document what should not be planned, touched, or proposed in early project builds.', group: 'Shared Constraints' },
  { key: 'clientDependencies', label: 'Client-side dependencies', placeholder: 'Record assets, approvals, access, or information the client still owes before work can proceed.', group: 'Shared Constraints' },
  { key: 'approvalTurnaround', label: 'Approval turnaround expectation', placeholder: 'Example: 1 business day, 3-5 business days, same-day if urgent.', control: 'input', group: 'Shared Constraints' },
  { key: 'platformTargets', label: 'Platform targets', placeholder: 'Example: iOS, Android, web app, Shopify storefront, admin dashboard, API-only.', group: 'Platform Development' },
  { key: 'externalSystemsIntegrations', label: 'External systems / integrations', placeholder: 'Document known integrations, external systems, data sources, or third-party dependencies.', group: 'Platform Development' },
  { key: 'trustComplianceRequirements', label: 'Trust / compliance / moderation', placeholder: 'Capture trust, safety, moderation, legal, privacy, or compliance expectations if known.', group: 'Platform Development' },
  { key: 'monetizationCommercialModel', label: 'Monetization / commercial model', placeholder: 'Describe subscriptions, commissions, lead flow, purchases, or other commercial mechanics if known.', group: 'Platform Development' },
  { key: 'brandStateGuidelines', label: 'Brand state / guidelines', placeholder: 'Describe the current brand system, visual direction, or whether brand guidance is still undefined.', group: 'Assets & Design' },
  { key: 'designAssetsReadiness', label: 'Design assets readiness', placeholder: 'List what assets exist today and what is still missing, such as logos, imagery, copy, or wireframes.', group: 'Assets & Design' },
  { key: 'marketingChannelsCadence', label: 'Channels / cadence', placeholder: 'Capture the marketing channels in play and any expected publishing or reporting cadence.', group: 'Managed Marketing' },
  { key: 'campaignOffersPromotions', label: 'Campaigns / offers / promotions', placeholder: 'Describe current or planned campaigns, offers, launches, or promotions if relevant.', group: 'Managed Marketing' },
  { key: 'paidMediaBudgetSignals', label: 'Paid media / budget signals', placeholder: 'Capture any known paid media expectations, budget bands, or channel commitments.', group: 'Managed Marketing' },
];
const ONBOARDING_PROFILE_GROUP_ORDER = [
  'Core Discovery',
  'Shared Constraints',
  'Platform Development',
  'Assets & Design',
  'Managed Marketing',
] as const;
const assessOnboardingKickoffReadiness = (
  profile: Record<keyof OrganizationMeetingOnboardingProfile, string>,
) => {
  const answered = (value: string | null | undefined) => asText(value).length > 0;
  const signals = [
    ['Business overview', profile.businessOverview],
    ['Client goals', profile.clientGoals],
    ['Services in scope', profile.servicesInScope],
    ['Project / engagement type', profile.projectOrEngagementType || profile.immediatePriorities],
    ['Target users / roles', profile.targetUsersAndRoles || profile.stakeholders],
    ['Core workflows / journeys', profile.coreWorkflowsJourneys || profile.firstMilestoneDeliverables],
    ['Current state / baseline', profile.currentState],
    ['Constraints / dependencies', profile.constraintsDependencies || profile.clientDependencies],
  ] as const;
  const answeredSignals = signals.filter(([, value]) => answered(value)).map(([label]) => label);
  const missingSignals = signals.filter(([, value]) => !answered(value)).map(([label]) => label);
  const status =
    answeredSignals.length >= 6 ? 'Strong' : answeredSignals.length >= 4 ? 'Partial' : 'Thin';
  const tone =
    status === 'Strong' ? '#7dd3a7' : status === 'Partial' ? '#f0c674' : '#f28b82';
  const summary =
    status === 'Strong'
      ? 'PM-ready for a confident discovery recommendation.'
      : status === 'Partial'
        ? 'Usable for Step 1, but likely still needs explicit clarification work.'
        : 'Thin intake. Expect the PM to favor a clarification or discovery-first project.';
  return { status, tone, summary, answeredSignals, missingSignals };
};
const normalizeOnboardingProfile = (
  value: Partial<Record<keyof OrganizationMeetingOnboardingProfile, unknown>> | null | undefined,
): Record<keyof OrganizationMeetingOnboardingProfile, string> => ({
  businessOverview: asText(value?.businessOverview),
  clientGoals: asText(value?.clientGoals),
  successMetrics: asText(value?.successMetrics),
  stakeholders: asText(value?.stakeholders),
  servicesInScope: asText(value?.servicesInScope),
  immediatePriorities: asText(value?.immediatePriorities),
  firstMilestoneDeliverables: asText(value?.firstMilestoneDeliverables),
  projectOrEngagementType: asText(value?.projectOrEngagementType),
  targetUsersAndRoles: asText(value?.targetUsersAndRoles),
  coreWorkflowsJourneys: asText(value?.coreWorkflowsJourneys),
  geographyMarketScope: asText(value?.geographyMarketScope),
  blockingClarifications: asText(value?.blockingClarifications),
  clarificationOwners: asText(value?.clarificationOwners),
  proceedabilityAssessment: asText(value?.proceedabilityAssessment),
  recommendedDiscoveryOutcome: asText(value?.recommendedDiscoveryOutcome),
  constraintsDependencies: asText(value?.constraintsDependencies),
  accessAssets: asText(value?.accessAssets),
  communicationPreferences: asText(value?.communicationPreferences),
  risksSensitivities: asText(value?.risksSensitivities),
  launchDate: asText(value?.launchDate),
  firstMilestoneTargetDate: asText(value?.firstMilestoneTargetDate),
  primaryContact: asText(value?.primaryContact),
  primaryApprover: asText(value?.primaryApprover),
  currentState: asText(value?.currentState),
  pastAttempts: asText(value?.pastAttempts),
  priorityRanking: asText(value?.priorityRanking),
  outOfScope: asText(value?.outOfScope),
  clientDependencies: asText(value?.clientDependencies),
  approvalTurnaround: asText(value?.approvalTurnaround),
  platformTargets: asText(value?.platformTargets),
  externalSystemsIntegrations: asText(value?.externalSystemsIntegrations),
  trustComplianceRequirements: asText(value?.trustComplianceRequirements),
  monetizationCommercialModel: asText(value?.monetizationCommercialModel),
  brandStateGuidelines: asText(value?.brandStateGuidelines),
  designAssetsReadiness: asText(value?.designAssetsReadiness),
  marketingChannelsCadence: asText(value?.marketingChannelsCadence),
  campaignOffersPromotions: asText(value?.campaignOffersPromotions),
  paidMediaBudgetSignals: asText(value?.paidMediaBudgetSignals),
});
const toOnboardingProfilePayload = (
  value: Record<keyof OrganizationMeetingOnboardingProfile, string>,
): OrganizationMeetingOnboardingProfile | null => {
  const normalized = normalizeOnboardingProfile(value);
  if (!Object.values(normalized).some(Boolean)) return null;
  return {
    businessOverview: normalized.businessOverview || null,
    clientGoals: normalized.clientGoals || null,
    successMetrics: normalized.successMetrics || null,
    stakeholders: normalized.stakeholders || null,
    servicesInScope: normalized.servicesInScope || null,
    immediatePriorities: normalized.immediatePriorities || null,
    firstMilestoneDeliverables: normalized.firstMilestoneDeliverables || null,
    projectOrEngagementType: normalized.projectOrEngagementType || null,
    targetUsersAndRoles: normalized.targetUsersAndRoles || null,
    coreWorkflowsJourneys: normalized.coreWorkflowsJourneys || null,
    geographyMarketScope: normalized.geographyMarketScope || null,
    blockingClarifications: normalized.blockingClarifications || null,
    clarificationOwners: normalized.clarificationOwners || null,
    proceedabilityAssessment: normalized.proceedabilityAssessment || null,
    recommendedDiscoveryOutcome: normalized.recommendedDiscoveryOutcome || null,
    constraintsDependencies: normalized.constraintsDependencies || null,
    accessAssets: normalized.accessAssets || null,
    communicationPreferences: normalized.communicationPreferences || null,
    risksSensitivities: normalized.risksSensitivities || null,
    launchDate: normalized.launchDate || null,
    firstMilestoneTargetDate: normalized.firstMilestoneTargetDate || null,
    primaryContact: normalized.primaryContact || null,
    primaryApprover: normalized.primaryApprover || null,
    currentState: normalized.currentState || null,
    pastAttempts: normalized.pastAttempts || null,
    priorityRanking: normalized.priorityRanking || null,
    outOfScope: normalized.outOfScope || null,
    clientDependencies: normalized.clientDependencies || null,
    approvalTurnaround: normalized.approvalTurnaround || null,
    platformTargets: normalized.platformTargets || null,
    externalSystemsIntegrations: normalized.externalSystemsIntegrations || null,
    trustComplianceRequirements:
      normalized.trustComplianceRequirements || null,
    monetizationCommercialModel:
      normalized.monetizationCommercialModel || null,
    brandStateGuidelines: normalized.brandStateGuidelines || null,
    designAssetsReadiness: normalized.designAssetsReadiness || null,
    marketingChannelsCadence: normalized.marketingChannelsCadence || null,
    campaignOffersPromotions: normalized.campaignOffersPromotions || null,
    paidMediaBudgetSignals: normalized.paidMediaBudgetSignals || null,
  };
};
const createEmptyMeetingNoteDraft = (): OrganizationMeetingNoteDraft => ({
  summary: '',
  clientUpdates: '',
  decisions: '',
  nextSteps: '',
  internalContext: '',
  onboardingProfile: { ...EMPTY_ONBOARDING_PROFILE },
});
const createEmptyMeetingEventDraft = (): OrganizationMeetingEventDraft => ({
  hostUserUid: '',
  meetingType: 'general_meeting',
  startsAtLocal: '',
  durationMinutes: 30,
  title: '',
  description: '',
  serviceContractId: '',
  meetingLinks: [''],
  createDiscordMeeting: false,
});
const normalizeMeetingLinkDrafts = (value: string[] | null | undefined): string[] => {
  const source = Array.isArray(value) ? value : [];
  const cleaned = source.map((entry) => asText(entry)).filter(Boolean);
  const unique = Array.from(new Set(cleaned));
  return unique.length ? unique : [''];
};
const normalizeCreateMeetingLinkDrafts = (value: string[] | null | undefined): string[] => {
  const source = Array.isArray(value) ? value : [];
  return source.map((entry) => (typeof entry === 'string' ? entry : '')).filter((_, index, array) => index < array.length);
};
const toDateTimeLocalValue = (value: string | null | undefined) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  const pad = (entry: number) => String(entry).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};
const toRequestedDateTimeLocalValue = (value: string | null | undefined) => {
  const requestedDate = asText(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? `${requestedDate}T12:00` : '';
};
const toMeetingEventDraft = (meeting: OrganizationScheduledMeeting | null | undefined): OrganizationMeetingEventDraft => ({
  hostUserUid: '',
  meetingType: meeting?.meetingType ?? 'general_meeting',
  startsAtLocal: meeting?.requestStatus === 'pending'
    ? toRequestedDateTimeLocalValue(meeting?.requestedForDate) || toDateTimeLocalValue(meeting?.startsAt)
    : toDateTimeLocalValue(meeting?.startsAt),
  durationMinutes: (() => {
    const startsAt = meeting?.startsAt ? new Date(meeting.startsAt) : null;
    const endsAt = meeting?.endsAt ? new Date(meeting.endsAt) : null;
    if (!startsAt || !endsAt) return 30;
    const delta = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
    return Number.isFinite(delta) && delta >= 15 ? delta : 30;
  })(),
  title: asText(meeting?.title),
  description: asText(meeting?.description),
  serviceContractId: asText(meeting?.serviceContractId),
  meetingLinks: normalizeMeetingLinkDrafts(meeting?.meetingLinks),
  createDiscordMeeting: Boolean(meeting?.discordMeeting),
});
const normalizeMeetingEventDraft = (draft: OrganizationMeetingEventDraft): OrganizationMeetingEventDraft => ({
  hostUserUid: asText(draft.hostUserUid),
  meetingType: draft.meetingType || 'general_meeting',
  startsAtLocal: asText(draft.startsAtLocal),
  durationMinutes: Number(draft.durationMinutes) || 30,
  title: asText(draft.title),
  description: asText(draft.description),
  serviceContractId: asText(draft.serviceContractId),
  meetingLinks: normalizeMeetingLinkDrafts(draft.meetingLinks),
  createDiscordMeeting: draft.createDiscordMeeting === true,
});
const toUserFacingLabel = (value: string | null | undefined) => asText(value) || '--';
const toMeetingLinkHref = (value: string) => {
  const raw = asText(value);
  if (!raw) return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
};
const buildMeetingSnippet = (meeting: OrganizationScheduledMeeting) =>
  asText(meeting.description) || `Scheduled ${SCHEDULE_MEETING_TYPE_LABELS[meeting.meetingType]} for internal and client coordination.`;
const buildMeetingParticipants = (meeting: OrganizationScheduledMeeting) => {
  const labels = meeting.attendees
    .map((attendee) => asText(attendee.name) || asText(attendee.email))
    .filter(Boolean);
  if (!labels.length) return 'Participants not assigned';
  if (labels.length <= 2) return labels.join(' | ');
  return `${labels.slice(0, 2).join(' | ')} +${labels.length - 2}`;
};
const formatMeetingServiceLabel = (value: string | null | undefined) => (
  asText(value) || 'Unassigned contract'
);
function normalizeOrganizationServiceCode(value: string | null | undefined): OrganizationServiceTabCode | null {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'marketing' || normalized === 'svc_marketing') return 'marketing';
  if (normalized === 'platform' || normalized === 'svc_platform') return 'platform';
  if (normalized === 'assets_design' || normalized === 'assets/design' || normalized === 'svc_assets_design') return 'assets_design';
  return null;
}
const inferServiceTab = (contract: OrganizationServiceContract): OrganizationServiceTabCode | null => {
  const direct = normalizeOrganizationServiceCode(contract.serviceCode);
  if (direct) return direct;
  const text = `${contract.serviceName ?? ''} ${contract.primaryTeamLabels.join(' ')}`.toLowerCase();
  if (text.includes('marketing')) return 'marketing';
  if (text.includes('platform') || text.includes('development')) return 'platform';
  if (text.includes('asset') || text.includes('design')) return 'assets_design';
  return null;
};

function normalizeOrganizations(raw: unknown): OrganizationRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item): OrganizationRecord | null => {
    if (!item || typeof item !== 'object') return null;
    const rec = item as Record<string, unknown>;
    const id = asText(rec.id) || asText(rec.orgId) || asText(rec.organizationId);
    const name = asText(rec.name) || asText(rec.orgName) || '-- Untitled organization --';
    if (!id && !name) return null;
    const ownerEmail = asText(rec.ownerEmail) || asText(rec.primaryContactEmail);
    const status = asText(rec.primarySubscriptionStatus) || asText(rec.subscriptionStatus) || null;
    return {
      id: id || name.toLowerCase().replace(/\s+/g, '-'),
      name,
      organizationType:
        asText(rec.organizationType).toLowerCase() === 'business'
          ? 'business'
          : 'individual',
      businessName: asText(rec.businessName) || null,
      memberCount: Number(rec.memberCount) || 0,
      ownerDisplay: asText(rec.ownerName) || emailLocalPart(ownerEmail),
      ownerEmail: ownerEmail || '--',
      logoURL: asText(rec.logoURL) || null,
      assignedServiceTeam: asText(rec.assignedServiceTeam) || asText(rec.primaryServiceTeam) || null,
      primaryServiceActive: parseOptionalBoolean(rec.primaryServiceActive) ?? deriveActiveFromStatus(status),
      primarySubscriptionStatus: status,
      isSuspended: parseOptionalBoolean(rec.isSuspended) === true,
    };
  }).filter((item): item is OrganizationRecord => Boolean(item));
}

function normalizeOrganizationMember(raw: unknown): OrganizationMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const uid = asText(rec.uid);
  const userId = asText(rec.userId);
  const displayName = asText(rec.displayName) || emailLocalPart(asText(rec.email));
  if (!uid && !userId && !displayName) return null;
  return {
    uid,
    userId,
    displayName: displayName || '--',
    email: asText(rec.email) || null,
    photoURL: asText(rec.photoURL) || null,
    contactPhone: asText(rec.contactPhone) || null,
    role: asText(rec.role) || 'member',
    isPrimaryAccountHolder: parseOptionalBoolean(rec.isPrimaryAccountHolder) === true,
  };
}

function normalizeOrganizationDetails(raw: unknown): OrganizationDetails | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = asText(rec.id) || asText(rec.orgId) || asText(rec.organizationId);
  const name = asText(rec.name) || '-- Untitled organization --';
  if (!id && !name) return null;
  const members = Array.isArray(rec.members) ? rec.members.map(normalizeOrganizationMember).filter((entry): entry is OrganizationMember => Boolean(entry)) : [];
  const health = rec.accountHealth && typeof rec.accountHealth === 'object' ? rec.accountHealth as Record<string, unknown> : null;
  const serviceContracts = Array.isArray(rec.serviceContracts)
    ? rec.serviceContracts.map((entry): OrganizationServiceContract | null => {
        if (!entry || typeof entry !== 'object') return null;
        const contract = entry as Record<string, unknown>;
        const contractId = asText(contract.id);
        if (!contractId) return null;
        return {
          id: contractId,
          status: asText(contract.status) || '--',
          primaryTeamLabels: Array.isArray(contract.primaryTeamLabels) ? contract.primaryTeamLabels.map((value) => asText(value)).filter(Boolean) : [],
          serviceCode: asText(contract.serviceCode) || null,
          serviceName: asText(contract.serviceName) || null,
          tierName: asText(contract.tierName) || null,
          selectedServiceOfferings: Array.isArray(contract.selectedServiceOfferings) ? contract.selectedServiceOfferings.map((value) => asText(value)).filter(Boolean) : [],
          selectedServiceOfferingCodes: Array.isArray(contract.selectedServiceOfferingCodes) ? contract.selectedServiceOfferingCodes.map((value) => asText(value)).filter(Boolean) : [],
          offeringEntitlements: Array.isArray(contract.offeringEntitlements)
            ? contract.offeringEntitlements
              .map((value) => {
                if (!value || typeof value !== 'object') return null;
                const record = value as Record<string, unknown>;
                const offeringCode = asText(record.offeringCode);
                const offeringName = asText(record.offeringName);
                if (!offeringCode || !offeringName) return null;
                return { offeringCode, offeringName };
              })
              .filter((entry): entry is OrganizationServiceContract['offeringEntitlements'][number] => Boolean(entry))
            : [],
          addOnNames: Array.isArray(contract.addOnNames) ? contract.addOnNames.map((value) => asText(value)).filter(Boolean) : [],
          contractTerm: asText(contract.contractTerm) || null,
          slaPolicies: Array.isArray(contract.slaPolicies) ? contract.slaPolicies.map((policy) => {
            if (!policy || typeof policy !== 'object') return null;
            const value = policy as Record<string, unknown>;
            return { name: asText(value.name) || '--', responseTargetMinutes: parseOptionalNumber(value.responseTargetMinutes), resolutionTargetMinutes: parseOptionalNumber(value.resolutionTargetMinutes), targetCompletionMinutes: parseOptionalNumber(value.targetCompletionMinutes) };
          }).filter((policy): policy is OrganizationServiceContract['slaPolicies'][number] => Boolean(policy)) : [],
          contractedMonthlyMinutesMin: parseOptionalNumber(contract.contractedMonthlyMinutesMin),
          contractedMonthlyMinutesMax: parseOptionalNumber(contract.contractedMonthlyMinutesMax),
          consumedBillableMinutes: Number(contract.consumedBillableMinutes) || 0,
          liveActiveMinutes: Number(contract.liveActiveMinutes) || 0,
          blockedBillableMinutes: Number(contract.blockedBillableMinutes) || 0,
          plannedRemainingEstimateMinutes: Number(contract.plannedRemainingEstimateMinutes) || 0,
          totalEstimatedMinutes: Number(contract.totalEstimatedMinutes) || 0,
          remainingBillableMinutesMin: parseOptionalNumber(contract.remainingBillableMinutesMin),
          remainingBillableMinutesMax: parseOptionalNumber(contract.remainingBillableMinutesMax),
          withinTargetCount: Number(contract.withinTargetCount) || 0,
          outsideTargetCount: Number(contract.outsideTargetCount) || 0,
          trackedTicketCount: Number(contract.trackedTicketCount) || 0,
          usagePeriodStartAt: asText(contract.usagePeriodStartAt) || null,
          usagePeriodEndAt: asText(contract.usagePeriodEndAt) || null,
          usageMeasuredThroughAt: asText(contract.usageMeasuredThroughAt) || null,
        };
      }).filter((entry): entry is OrganizationServiceContract => Boolean(entry))
    : [];
  return {
    id: id || name.toLowerCase().replace(/\s+/g, '-'),
    name,
    organizationType: asText(rec.organizationType).toLowerCase() === 'business' ? 'business' : 'individual',
    businessName: asText(rec.businessName) || null,
    industry: asText(rec.industry) || null,
    contactEmail: asText(rec.contactEmail) || null,
    contactPhone: asText(rec.contactPhone) || null,
    website: asText(rec.website) || null,
    address: asText(rec.address) || null,
    logoURL: asText(rec.logoURL) || null,
    ownerDisplay: asText(rec.ownerDisplay) || '--',
    ownerEmail: asText(rec.ownerEmail) || null,
    ownerContactPhone: asText(rec.ownerContactPhone) || null,
    memberCount: Number(rec.memberCount) || members.length,
    pendingInviteCount: Number(rec.pendingInviteCount) || 0,
    projectCount: Number(rec.projectCount) || 0,
    ticketCount: Number(rec.ticketCount) || 0,
    assignedServiceTeam: asText(rec.assignedServiceTeam) || null,
    assignedServiceTeams: Array.isArray(rec.assignedServiceTeams) ? rec.assignedServiceTeams.map((value) => asText(value)).filter(Boolean) : [],
    primarySubscriptionStatus: asText(rec.primarySubscriptionStatus) || null,
    primaryServiceActive: parseOptionalBoolean(rec.primaryServiceActive),
    isSuspended: parseOptionalBoolean(rec.isSuspended) === true,
    suspendedAt: asText(rec.suspendedAt) || null,
    suspendedByUserId: asText(rec.suspendedByUserId) || null,
    purchasedServices: Array.isArray(rec.purchasedServices) ? rec.purchasedServices.map((value) => asText(value)).filter(Boolean) : [],
    accountHealth: health ? {
      activeServiceCount: Number(health.activeServiceCount) || 0,
      contractedMonthlyMinutesMin: parseOptionalNumber(health.contractedMonthlyMinutesMin),
      contractedMonthlyMinutesMax: parseOptionalNumber(health.contractedMonthlyMinutesMax),
      consumedBillableMinutes: Number(health.consumedBillableMinutes) || 0,
      liveActiveMinutes: Number(health.liveActiveMinutes) || 0,
      blockedBillableMinutes: Number(health.blockedBillableMinutes) || 0,
      plannedRemainingEstimateMinutes: Number(health.plannedRemainingEstimateMinutes) || 0,
      totalEstimatedMinutes: Number(health.totalEstimatedMinutes) || 0,
      remainingBillableMinutesMin: parseOptionalNumber(health.remainingBillableMinutesMin),
      remainingBillableMinutesMax: parseOptionalNumber(health.remainingBillableMinutesMax),
      withinTargetCount: Number(health.withinTargetCount) || 0,
      outsideTargetCount: Number(health.outsideTargetCount) || 0,
      trackedTicketCount: Number(health.trackedTicketCount) || 0,
      usagePeriodStartAt: asText(health.usagePeriodStartAt) || null,
      usagePeriodEndAt: asText(health.usagePeriodEndAt) || null,
      usageMeasuredThroughAt: asText(health.usageMeasuredThroughAt) || null,
    } : null,
    serviceContracts,
    members,
    primaryAccountHolder: normalizeOrganizationMember(rec.primaryAccountHolder) ?? members.find((member) => member.isPrimaryAccountHolder) ?? null,
  };
}

function normalizeScheduleMeeting(raw: unknown): OrganizationScheduledMeeting | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = asText(rec.id);
  if (!id) return null;
  const rawMeetingType = asText(rec.meetingType);
  const meetingType = (SCHEDULE_MEETING_TYPES as readonly string[]).includes(rawMeetingType)
    ? (rawMeetingType as ScheduleMeetingType)
    : 'general_meeting';
  const statusRaw = asText(rec.status).toLowerCase();
  const requestStatusRaw = asText(rec.requestStatus).toLowerCase();
  const requestStatus =
    requestStatusRaw === 'pending' || requestStatusRaw === 'confirmed'
      ? requestStatusRaw
      : null;
  const status = requestStatus === 'pending'
    ? 'requested'
    : statusRaw === 'canceled' || statusRaw === 'completed' || statusRaw === 'no_show'
      ? statusRaw
      : 'scheduled';
  return {
    id,
    orgId: asText(rec.orgId) || null,
    serviceContractId: asText(rec.serviceContractId) || null,
    serviceCode: asText(rec.serviceCode) || null,
    serviceName: asText(rec.serviceName) || null,
    hostUserId: asText(rec.hostUserId) || null,
    organizerUserId: asText(rec.organizerUserId) || null,
    clientUserId: asText(rec.clientUserId) || null,
    slotId: asText(rec.slotId) || null,
    meetingType,
    title: asText(rec.title) || SCHEDULE_MEETING_TYPE_LABELS[meetingType],
    description: asText(rec.description) || null,
    meetingLinks: Array.isArray(rec.meetingLinks)
      ? rec.meetingLinks.map((entry) => asText(entry)).filter(Boolean)
      : [],
    discordMeeting: rec.discordMeeting && typeof rec.discordMeeting === 'object'
      ? (() => {
          const value = rec.discordMeeting as Record<string, unknown>;
          const scheduledEventId = asText(value.scheduledEventId);
          const scheduledEventUrl = asText(value.scheduledEventUrl);
          const textChannelId = asText(value.textChannelId);
          const textChannelUrl = asText(value.textChannelUrl);
          const voiceChannelId = asText(value.voiceChannelId);
          const voiceChannelName = asText(value.voiceChannelName) || 'discord-meeting';
          const voiceChannelUrl = asText(value.voiceChannelUrl);
          const syncedAt = asText(value.syncedAt);
          if (!scheduledEventId || !scheduledEventUrl || !textChannelId || !textChannelUrl || !voiceChannelId || !voiceChannelUrl || !syncedAt) {
            return null;
          }
          return {
            scheduledEventId,
            scheduledEventUrl,
            textChannelId,
            textChannelUrl,
            voiceChannelId,
            voiceChannelName,
            voiceChannelUrl,
            messageId: asText(value.messageId) || null,
            messageUrl: asText(value.messageUrl) || null,
            syncedAt,
          };
        })()
      : null,
    startsAt: asText(rec.startsAt),
    endsAt: asText(rec.endsAt),
    timezone: asText(rec.timezone) || 'UTC',
    status,
    requestStatus,
    requestedForDate: asText(rec.requestedForDate) || null,
    requestedAt: asText(rec.requestedAt) || null,
    attendees: Array.isArray(rec.attendees) ? rec.attendees.map((attendee) => {
      if (!attendee || typeof attendee !== 'object') return null;
      const value = attendee as Record<string, unknown>;
      const attendeeId = asText(value.id);
      if (!attendeeId) return null;
      return { id: attendeeId, userId: asText(value.userId) || null, email: asText(value.email) || null, name: asText(value.name) || null, role: asText(value.role) || null };
    }).filter((attendee): attendee is OrganizationScheduledMeeting['attendees'][number] => Boolean(attendee)) : [],
    createdAt: asText(rec.createdAt) || undefined,
    updatedAt: asText(rec.updatedAt) || undefined,
  };
}
function normalizeMeetingNote(raw: unknown): OrganizationMeetingNote | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = asText(rec.id);
  const eventId = asText(rec.eventId);
  if (!id || !eventId) return null;
  const onboardingProfile = toOnboardingProfilePayload(
    normalizeOnboardingProfile(rec.onboardingProfile as Partial<Record<keyof OrganizationMeetingOnboardingProfile, unknown>> | undefined),
  );
  return {
    id,
    eventId,
    orgId: asText(rec.orgId) || null,
    serviceContractId: asText(rec.serviceContractId) || null,
    serviceCode: asText(rec.serviceCode) || null,
    serviceName: asText(rec.serviceName) || null,
    summary: asText(rec.summary) || null,
    clientUpdates: asText(rec.clientUpdates) || null,
    decisions: asText(rec.decisions) || null,
    nextSteps: asText(rec.nextSteps) || null,
    internalContext: asText(rec.internalContext) || null,
    onboardingProfile,
    createdByUserId: asText(rec.createdByUserId) || null,
    updatedByUserId: asText(rec.updatedByUserId) || null,
    createdAt: asText(rec.createdAt),
    updatedAt: asText(rec.updatedAt),
  };
}
const toMeetingNoteDraft = (note: OrganizationMeetingNote | null | undefined): OrganizationMeetingNoteDraft => ({
  summary: note?.summary ?? '',
  clientUpdates: note?.clientUpdates ?? '',
  decisions: note?.decisions ?? '',
  nextSteps: note?.nextSteps ?? '',
  internalContext: note?.internalContext ?? '',
  onboardingProfile: normalizeOnboardingProfile(note?.onboardingProfile),
});
const normalizeMeetingNoteDraft = (draft: OrganizationMeetingNoteDraft): OrganizationMeetingNoteDraft => ({
  summary: asText(draft.summary),
  clientUpdates: asText(draft.clientUpdates),
  decisions: asText(draft.decisions),
  nextSteps: asText(draft.nextSteps),
  internalContext: asText(draft.internalContext),
  onboardingProfile: normalizeOnboardingProfile(draft.onboardingProfile),
});

const getPreferredServiceTab = (details: OrganizationDetails | null): OrganizationServiceTabCode => {
  if (!details) return 'marketing';
  return details.serviceContracts.map(inferServiceTab).find(Boolean) ?? details.purchasedServices.map(normalizeOrganizationServiceCode).find(Boolean) ?? 'marketing';
};
const getServiceContract = (details: OrganizationDetails | null, serviceTab: OrganizationServiceTabCode) => details?.serviceContracts.find((contract) => inferServiceTab(contract) === serviceTab) ?? null;
const getContractTeamLabels = (
  details: OrganizationDetails | null,
  contract: OrganizationServiceContract,
): string[] => {
  if (contract.primaryTeamLabels.length) {
    return contract.primaryTeamLabels;
  }
  if ((details?.serviceContracts.length ?? 0) === 1 && (details?.assignedServiceTeams.length ?? 0) > 0) {
    return details?.assignedServiceTeams ?? [];
  }
  return [];
};

const cellStyle: React.CSSProperties = { minWidth: 0, border: '1px solid rgba(255,255,255,.08)', borderRadius: 12, padding: 10, display: 'grid', gap: 2 };
const labelStyle: React.CSSProperties = { color: '#9aa4b2', fontSize: 10, fontWeight: 900, letterSpacing: '.24px', textTransform: 'uppercase' };
const valueStyle: React.CSSProperties = { color: '#f3f4f6', fontSize: 22, fontWeight: 900, lineHeight: 1.1 };
const ORGANIZATION_PAGE_SIZE = 20;
const createOrganizationSectionLabelStyle: React.CSSProperties = { color: '#9aa4b2', fontSize: 11, fontWeight: 900, letterSpacing: '.24px', textTransform: 'uppercase' };
const createOrganizationFieldLabelStyle: React.CSSProperties = { color: '#d7ddeb', fontSize: 11, fontWeight: 800 };
const createOrganizationInputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', minHeight: 40, borderRadius: 10, border: '1px solid rgba(255,255,255,.12)', background: 'rgba(255,255,255,.03)', color: '#f3f4f6', padding: '0 12px', fontSize: 13, fontWeight: 700 };
const createEmptyOrganizationDraft = (): CreateOrganizationDraft => ({
  businessName: '',
  industry: '',
  contactPhone: '',
  website: '',
  address: '',
});

export default function TeamsOrganizationsPage() {
  const { user, role } = useAuth();
  const [query, setQuery] = useState('');
  const [organizationPage, setOrganizationPage] = useState(1);
  const [rows, setRows] = useState<OrganizationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [serviceStateFilter, setServiceStateFilter] = useState<ServiceStateFilter>('all');
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('overview');
  const [serviceTab, setServiceTab] = useState<OrganizationServiceTabCode>('marketing');
  const [orgActionsOpen, setOrgActionsOpen] = useState(false);
  const [suspendSaving, setSuspendSaving] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [detailsCache, setDetailsCache] = useState<Record<string, OrganizationDetails>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [meetingsByOrgId, setMeetingsByOrgId] = useState<Record<string, OrganizationScheduledMeeting[]>>({});
  const [meetingsLoading, setMeetingsLoading] = useState<Record<string, boolean>>({});
  const [meetingErrorsByOrgId, setMeetingErrorsByOrgId] = useState<Record<string, string | null>>({});
  const [meetingNotesByEventId, setMeetingNotesByEventId] = useState<Record<string, OrganizationMeetingNote | null>>({});
  const [internalScheduleUsers, setInternalScheduleUsers] = useState<InternalScheduleUser[]>([]);
  const [internalScheduleUsersLoading, setInternalScheduleUsersLoading] = useState(false);
  const [meetingModal, setMeetingModal] = useState<MeetingModalState>(null);
  const [meetingDraft, setMeetingDraft] = useState<OrganizationMeetingDraft>(createEmptyMeetingDraft(user?.uid ?? ''));
  const [meetingFormError, setMeetingFormError] = useState<string | null>(null);
  const [meetingSaving, setMeetingSaving] = useState(false);
  const [meetingEventDraft, setMeetingEventDraft] = useState<OrganizationMeetingEventDraft>(createEmptyMeetingEventDraft());
  const [meetingEventSaving, setMeetingEventSaving] = useState(false);
  const [meetingEventError, setMeetingEventError] = useState<string | null>(null);
  const [meetingDiscordSyncing, setMeetingDiscordSyncing] = useState(false);
  const [meetingDiscordRemoving, setMeetingDiscordRemoving] = useState(false);
  const [meetingDiscordError, setMeetingDiscordError] = useState<string | null>(null);
  const [meetingNoteDraft, setMeetingNoteDraft] = useState<OrganizationMeetingNoteDraft>(createEmptyMeetingNoteDraft());
  const [meetingNoteLoading, setMeetingNoteLoading] = useState(false);
  const [meetingNoteSaving, setMeetingNoteSaving] = useState(false);
  const [meetingNoteError, setMeetingNoteError] = useState<string | null>(null);
  const [meetingInternalContextGenerating, setMeetingInternalContextGenerating] = useState(false);
  const [meetingInternalContextError, setMeetingInternalContextError] = useState<string | null>(null);
  const [orgAttendeePickerOpen, setOrgAttendeePickerOpen] = useState(false);
  const [internalAttendeePickerOpen, setInternalAttendeePickerOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [memberInviteQuery, setMemberInviteQuery] = useState('');
  const [memberInviteResults, setMemberInviteResults] = useState<InvitePlatformClient[]>([]);
  const [memberInviteLoading, setMemberInviteLoading] = useState(false);
  const [memberInviteError, setMemberInviteError] = useState<string | null>(null);
  const [memberInviteEmail, setMemberInviteEmail] = useState('');
  const [memberInviteRole, setMemberInviteRole] = useState<MemberInviteRole>('member');
  const [memberInviteSearchOpen, setMemberInviteSearchOpen] = useState(false);
  const [memberInviteSaving, setMemberInviteSaving] = useState(false);
  const [createOrganizationOpen, setCreateOrganizationOpen] = useState(false);
  const [createOrganizationDraft, setCreateOrganizationDraft] = useState<CreateOrganizationDraft>(createEmptyOrganizationDraft());
  const [createOrganizationSaving, setCreateOrganizationSaving] = useState(false);
  const [createOrganizationError, setCreateOrganizationError] = useState<string | null>(null);
  const [createOrganizationIndustryOpen, setCreateOrganizationIndustryOpen] = useState(false);
  const [meetingStatusFilter, setMeetingStatusFilter] = useState<MeetingStatusFilter>('all');
  const [meetingSortOrder, setMeetingSortOrder] = useState<MeetingSortOrder>('newest');
  const [meetingServiceFilter, setMeetingServiceFilter] = useState<MeetingServiceFilter>('all');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState<MeetingTypeFilter>('all');
  const [meetingDeletePendingById, setMeetingDeletePendingById] = useState<Record<string, boolean>>({});
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const orgActionsRef = useRef<HTMLDivElement | null>(null);
  const meetingNoteRequestIdRef = useRef(0);
  const meetingsLoadingRef = useRef<Record<string, boolean>>({});
  const meetingModalBodyRef = useRef<HTMLDivElement | null>(null);
  const workspaceBodyRef = useRef<HTMLDivElement | null>(null);
  const createOrganizationIndustryRef = useRef<HTMLDivElement | null>(null);
  const memberInviteSearchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    meetingsLoadingRef.current = meetingsLoading;
  }, [meetingsLoading]);

  const loadOrganizations = useCallback(async (preferredOrgId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await apiFetch<any>('/api/admin/organizations');
      const normalized = normalizeOrganizations(payload?.organizations ?? payload);
      setRows(normalized);
      setSelectedOrgId((current) => {
        if (preferredOrgId && normalized.some((row) => row.id === preferredOrgId)) {
          return preferredOrgId;
        }
        if (current && normalized.some((row) => row.id === current)) {
          return current;
        }
        return normalized[0]?.id ?? null;
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!controlsRef.current?.contains(target)) {
        setFiltersOpen(false);
      }
      if (!orgActionsRef.current?.contains(target)) {
        setOrgActionsOpen(false);
      }
      if (!createOrganizationIndustryRef.current?.contains(target)) {
        setCreateOrganizationIndustryOpen(false);
      }
      if (!memberInviteSearchRef.current?.contains(target)) {
        setMemberInviteSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, []);

  useEffect(() => {
    if (!selectedOrgId || detailsCache[selectedOrgId]) return;
    let active = true;
    (async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const payload = await apiFetch<any>(`/api/admin/organizations/${encodeURIComponent(selectedOrgId)}`);
        if (!active) return;
        const normalized = normalizeOrganizationDetails(payload);
        if (!normalized) throw new Error('Invalid organization details response');
        setDetailsCache((prev) => ({ ...prev, [selectedOrgId]: normalized }));
        setServiceTab(getPreferredServiceTab(normalized));
      } catch (e: any) {
        if (active) setDetailsError(e?.message || 'Failed to load organization');
      } finally {
        if (active) setDetailsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedOrgId, detailsCache]);

  useEffect(() => {
    setOrgActionsOpen(false);
  }, [selectedOrgId, workspaceTab]);

  useEffect(() => {
    if ((workspaceTab as string) === 'actions') {
      setWorkspaceTab('overview');
    }
  }, [workspaceTab]);

  useEffect(() => {
    setMeetingServiceFilter('all');
    setMeetingTypeFilter('all');
  }, [selectedOrgId]);

  const closeCreateOrganizationModal = () => {
    setCreateOrganizationOpen(false);
    setCreateOrganizationDraft(createEmptyOrganizationDraft());
    setCreateOrganizationError(null);
    setCreateOrganizationIndustryOpen(false);
  };

  const submitCreateOrganization = async () => {
    const businessName = createOrganizationDraft.businessName.trim();
    const industry = createOrganizationDraft.industry.trim();
    if (!businessName) {
      setCreateOrganizationError('Business name is required.');
      return;
    }
    if (!industry) {
      setCreateOrganizationError('Industry is required.');
      return;
    }

    try {
      setCreateOrganizationSaving(true);
      setCreateOrganizationError(null);
      const payload = await apiFetch<any>('/api/admin/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName,
          industry,
          contactPhone: createOrganizationDraft.contactPhone.trim() || undefined,
          website: createOrganizationDraft.website.trim() || undefined,
          address: createOrganizationDraft.address.trim() || undefined,
        }),
      });
      const createdOrgId =
        typeof payload?.orgId === 'string' && payload.orgId.trim() ? payload.orgId.trim() : null;
      closeCreateOrganizationModal();
      if (createdOrgId) {
        setDetailsCache((prev) => {
          const next = { ...prev };
          delete next[createdOrgId];
          return next;
        });
      }
      await loadOrganizations(createdOrgId);
    } catch (e: any) {
      setCreateOrganizationError(e?.message || 'Failed to create organization');
    } finally {
      setCreateOrganizationSaving(false);
    }
  };

  const closeAddMemberModal = () => {
    if (memberInviteSaving) return;
    setCreateClientOpen(false);
    setMemberInviteQuery('');
    setMemberInviteResults([]);
    setMemberInviteLoading(false);
    setMemberInviteError(null);
    setMemberInviteEmail('');
    setMemberInviteRole('member');
    setMemberInviteSearchOpen(false);
  };

  const submitAddMemberInvite = async () => {
    const inviteEmail = memberInviteEmail.trim();
    const currentDetails = selectedOrgId ? detailsCache[selectedOrgId] ?? null : null;
    if (!currentDetails?.id) {
      setMemberInviteError('Select an organization before inviting members.');
      return;
    }
    if (!inviteEmail) {
      setMemberInviteError('Member email is required.');
      return;
    }

    const hasPrimaryAccountHolder = Boolean(
      currentDetails.primaryAccountHolder?.uid
        || currentDetails.primaryAccountHolder?.userId
        || currentDetails.primaryAccountHolder?.email
        || currentDetails.ownerEmail
        || currentDetails.members.some((member) => member.isPrimaryAccountHolder),
    );
    const invitedRole: MemberInviteRole =
      !hasPrimaryAccountHolder && memberInviteRole === 'owner' ? 'owner' : 'member';

    try {
      setMemberInviteSaving(true);
      setMemberInviteError(null);
      await apiFetch(`/api/admin/organizations/${encodeURIComponent(currentDetails.id)}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          role: invitedRole,
        }),
      });
      closeAddMemberModal();
      setDetailsCache((prev) => {
        const next = { ...prev };
        delete next[currentDetails.id];
        return next;
      });
      await loadOrganizations(currentDetails.id);
      window.alert(
        invitedRole === 'owner'
          ? `Primary account holder invite sent to ${inviteEmail}.`
          : `Member invite sent to ${inviteEmail}.`,
      );
    } catch (e: any) {
      setMemberInviteError(e?.message || 'Failed to send member invite');
    } finally {
      setMemberInviteSaving(false);
    }
  };

  useEffect(() => {
    if (!createClientOpen) {
      setMemberInviteResults([]);
      setMemberInviteLoading(false);
      setMemberInviteError(null);
      return;
    }
    const trimmedQuery = memberInviteQuery.trim();
    if (trimmedQuery.length < 2) {
      setMemberInviteResults([]);
      setMemberInviteLoading(false);
      setMemberInviteError(null);
      setMemberInviteSearchOpen(false);
      return;
    }

    let active = true;
    const timeoutId = window.setTimeout(() => {
      setMemberInviteLoading(true);
      setMemberInviteError(null);
      void (async () => {
        try {
          const payload = await apiFetch<any>(`/api/admin/users?role=client&page=0&q=${encodeURIComponent(trimmedQuery)}`);
          if (!active) return;
          const existingMemberEmails = new Set(
            ((selectedOrgId ? detailsCache[selectedOrgId]?.members : null) ?? [])
              .map((member) => (member.email ?? '').trim().toLowerCase())
              .filter(Boolean),
          );
          const normalized = (Array.isArray(payload?.users) ? payload.users : [])
            .map((entry: unknown) => {
              if (!entry || typeof entry !== 'object') return null;
              const record = entry as Record<string, unknown>;
              const email = String(record.email ?? '').trim();
              if (!email) return null;
              return {
                uid: String(record.id ?? '').trim(),
                email,
                name: typeof record.name === 'string' ? record.name : null,
                photoURL:
                  typeof record.photoURL === 'string'
                    ? record.photoURL
                    : typeof record.avatarUrl === 'string'
                      ? record.avatarUrl
                      : null,
                businessName:
                  typeof record.businessName === 'string' ? record.businessName : null,
              } satisfies InvitePlatformClient;
            })
            .filter((entry: InvitePlatformClient | null): entry is InvitePlatformClient => Boolean(entry))
            .filter((entry: InvitePlatformClient) => !existingMemberEmails.has(entry.email.trim().toLowerCase()));
          setMemberInviteResults(normalized);
        } catch (e: any) {
          if (!active) return;
          setMemberInviteError(e?.message || 'Failed to search platform clients');
          setMemberInviteResults([]);
        } finally {
          if (active) {
            setMemberInviteLoading(false);
          }
        }
      })();
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [createClientOpen, detailsCache, memberInviteQuery, selectedOrgId]);

  useEffect(() => {
    if (!createClientOpen) return;
    const currentDetails = selectedOrgId ? detailsCache[selectedOrgId] ?? null : null;
    const hasPrimaryAccountHolder = Boolean(
      currentDetails?.primaryAccountHolder?.uid
        || currentDetails?.primaryAccountHolder?.userId
        || currentDetails?.primaryAccountHolder?.email
        || currentDetails?.ownerEmail
        || currentDetails?.members.some((member) => member.isPrimaryAccountHolder),
    );
    if (hasPrimaryAccountHolder && memberInviteRole === 'owner') {
      setMemberInviteRole('member');
    }
  }, [createClientOpen, detailsCache, memberInviteRole, selectedOrgId]);

  const loadMeetingsForOrg = async (orgId: string) => {
    const payload = await apiFetch<any>(`/api/schedule/events?orgId=${encodeURIComponent(orgId)}`);
    const normalized = Array.isArray(payload)
      ? payload.map(normalizeScheduleMeeting).filter((entry): entry is OrganizationScheduledMeeting => Boolean(entry))
      : [];
    const sorted = normalized.sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime());
    setMeetingsByOrgId((prev) => ({ ...prev, [orgId]: sorted }));
    return sorted;
  };

  useEffect(() => {
    if (workspaceTab !== 'meetings' || !selectedOrgId || meetingsByOrgId[selectedOrgId] || meetingsLoadingRef.current[selectedOrgId]) return;
    let active = true;
    (async () => {
      setMeetingsLoading((prev) => ({ ...prev, [selectedOrgId]: true }));
      setMeetingErrorsByOrgId((prev) => ({ ...prev, [selectedOrgId]: null }));
      try {
        const normalized = await loadMeetingsForOrg(selectedOrgId);
        if (!active) return;
        setMeetingsByOrgId((prev) => ({ ...prev, [selectedOrgId]: normalized }));
      } catch (e: any) {
        if (active) {
          setMeetingErrorsByOrgId((prev) => ({
            ...prev,
            [selectedOrgId]: e?.message || 'Failed to load meetings',
          }));
        }
      } finally {
        if (active) setMeetingsLoading((prev) => ({ ...prev, [selectedOrgId]: false }));
      }
    })();
    return () => {
      active = false;
    };
  }, [workspaceTab, selectedOrgId, meetingsByOrgId]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    if (row.organizationType !== 'business') return false;
    const haystack = `${row.name} ${row.ownerDisplay} ${row.ownerEmail}`.toLowerCase();
    if (query.trim() && !haystack.includes(query.trim().toLowerCase())) return false;
    if (serviceStateFilter === 'active' && row.primaryServiceActive !== true) return false;
    if (serviceStateFilter === 'inactive' && row.primaryServiceActive !== false) return false;
    if (teamFilter === 'with_team' && !row.assignedServiceTeam) return false;
    if (teamFilter === 'without_team' && row.assignedServiceTeam) return false;
    return true;
  }), [query, rows, serviceStateFilter, teamFilter]);

  useEffect(() => {
    setOrganizationPage(1);
  }, [query, serviceStateFilter, teamFilter]);

  const organizationTotalPages = Math.max(1, Math.ceil(filteredRows.length / ORGANIZATION_PAGE_SIZE));

  useEffect(() => {
    if (organizationPage > organizationTotalPages) {
      setOrganizationPage(organizationTotalPages);
    }
  }, [organizationPage, organizationTotalPages]);

  const visibleRows = useMemo(
    () => filteredRows.slice((organizationPage - 1) * ORGANIZATION_PAGE_SIZE, organizationPage * ORGANIZATION_PAGE_SIZE),
    [filteredRows, organizationPage],
  );

  useEffect(() => {
    if (!visibleRows.length) return void setSelectedOrgId(null);
    if (selectedOrgId && visibleRows.some((row) => row.id === selectedOrgId)) return;
    setSelectedOrgId(visibleRows[0].id);
  }, [visibleRows, selectedOrgId]);

  useEffect(() => {
    if (workspaceTab !== 'ai_agent') return;
    workspaceBodyRef.current?.scrollTo({ top: 0 });
  }, [workspaceTab, selectedOrgId, serviceTab]);

  const selectedRow = visibleRows.find((row) => row.id === selectedOrgId) ?? null;
  const details = selectedOrgId ? detailsCache[selectedOrgId] ?? null : null;
  const organizationHasPrimaryAccountHolder = Boolean(
    details?.primaryAccountHolder?.uid
      || details?.primaryAccountHolder?.userId
      || details?.primaryAccountHolder?.email
      || details?.ownerEmail
      || details?.members.some((member) => member.isPrimaryAccountHolder),
  );
  const organizationPrimaryAccountHolderLabel =
    details?.primaryAccountHolder?.displayName
      || details?.primaryAccountHolder?.email
      || details?.ownerDisplay
      || details?.ownerEmail
      || null;
  useEffect(() => {
    if (!details) return;
    const preferredServiceTab = getPreferredServiceTab(details);
    setServiceTab((current) => (current === preferredServiceTab ? current : preferredServiceTab));
  }, [details]);
  const meetings = useMemo(
    () => (selectedOrgId ? meetingsByOrgId[selectedOrgId] ?? [] : []),
    [meetingsByOrgId, selectedOrgId],
  );
  const hasExistingOnboardingKickoff = useMemo(
    () => meetings.some((meeting) => meeting.meetingType === 'intro_call'),
    [meetings],
  );
  const organizationMeetingTypeOptions = useMemo(
    () => (
      hasExistingOnboardingKickoff
        ? ORGANIZATION_SCHEDULE_MEETING_TYPES.filter((value) => value !== 'intro_call')
        : ORGANIZATION_SCHEDULE_MEETING_TYPES
    ),
    [hasExistingOnboardingKickoff],
  );
  const serviceContractsById = useMemo(
    () => new Map((details?.serviceContracts ?? []).map((contract) => [contract.id, contract] as const)),
    [details?.serviceContracts],
  );
  const meetingServiceOptions = useMemo(() => {
    const options = new Map<string, { id: string; label: string; helper: string }>();
    (details?.serviceContracts ?? []).forEach((contract) => {
      options.set(contract.id, {
        id: contract.id,
        label: contract.serviceName || contract.tierName || contract.serviceCode || 'Service contract',
        helper: contract.selectedServiceOfferings[0] || contract.contractTerm || '',
      });
    });
    meetings.forEach((meeting) => {
      const contractId = asText(meeting.serviceContractId);
      if (!contractId || options.has(contractId)) return;
      options.set(contractId, {
        id: contractId,
        label: meeting.serviceName || meeting.serviceCode || 'Historical service contract',
        helper: '',
      });
    });
    return Array.from(options.values());
  }, [details?.serviceContracts, meetings]);
  const meetingTypeOptions = useMemo(
    () => (
      Array.from(new Set(meetings.map((meeting) => meeting.meetingType)))
        .sort((a, b) => SCHEDULE_MEETING_TYPE_LABELS[a].localeCompare(SCHEDULE_MEETING_TYPE_LABELS[b]))
    ),
    [meetings],
  );
  const visibleMeetings = useMemo(() => {
    const filtered = meetings.filter((meeting) => {
      if (meetingTypeFilter !== 'all' && meeting.meetingType !== meetingTypeFilter) {
        return false;
      }
      if (meetingStatusFilter !== 'all' && meeting.status !== meetingStatusFilter) {
        return false;
      }
      if (meetingServiceFilter === 'all') {
        return true;
      }
      if (meetingServiceFilter === 'unassigned') {
        return !meeting.serviceContractId;
      }
      return meeting.serviceContractId === meetingServiceFilter;
    });
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aTime = new Date(a.startsAt).getTime();
      const bTime = new Date(b.startsAt).getTime();
      if (meetingSortOrder === 'oldest') return aTime - bTime;
      if (meetingSortOrder === 'soonest') {
        const now = Date.now();
        const aFuture = aTime >= now;
        const bFuture = bTime >= now;
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        if (aFuture && bFuture) return aTime - bTime;
        return bTime - aTime;
      }
      return bTime - aTime;
    });
    return sorted;
  }, [meetingServiceFilter, meetingSortOrder, meetingStatusFilter, meetingTypeFilter, meetings]);
  const hasActiveMeetingFilters = meetingServiceFilter !== 'all'
    || meetingStatusFilter !== 'all'
    || meetingTypeFilter !== 'all';
  const meetingsError = selectedOrgId ? meetingErrorsByOrgId[selectedOrgId] ?? null : null;
  const selectedContract = getServiceContract(details, serviceTab);
  const filterCount = Number(serviceStateFilter !== 'all') + Number(teamFilter !== 'all');

  const activeMeetingRecord = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view') return null;
    return (meetingsByOrgId[meetingModal.orgId] ?? []).find((entry) => entry.id === meetingModal.meetingId) ?? null;
  }, [meetingModal, meetingsByOrgId]);
  const activeMeetingNote = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view') return undefined;
    return Object.prototype.hasOwnProperty.call(meetingNotesByEventId, meetingModal.meetingId)
      ? meetingNotesByEventId[meetingModal.meetingId]
      : undefined;
  }, [meetingModal, meetingNotesByEventId]);
  const activeMeetingOrganization = useMemo(() => {
    if (!meetingModal) return null;
    if (details?.id === meetingModal.orgId) return details;
    return detailsCache[meetingModal.orgId] ?? null;
  }, [details, detailsCache, meetingModal]);
  const selectedOrganizationAttendees = useMemo(
    () => (activeMeetingOrganization?.members ?? []).filter((member) => meetingDraft.orgMemberUserIds.includes(member.userId)),
    [activeMeetingOrganization?.members, meetingDraft.orgMemberUserIds],
  );
  const availableOrganizationAttendees = useMemo(
    () => (activeMeetingOrganization?.members ?? []).filter((member) => !meetingDraft.orgMemberUserIds.includes(member.userId)),
    [activeMeetingOrganization?.members, meetingDraft.orgMemberUserIds],
  );
  const selectedInternalAttendees = useMemo(
    () => internalScheduleUsers.filter((entry) => entry.userUid !== meetingDraft.hostUserUid && meetingDraft.internalAttendeeUids.includes(entry.userUid)),
    [internalScheduleUsers, meetingDraft.hostUserUid, meetingDraft.internalAttendeeUids],
  );
  const availableInternalAttendees = useMemo(
    () => internalScheduleUsers.filter((entry) => entry.userUid !== meetingDraft.hostUserUid && !meetingDraft.internalAttendeeUids.includes(entry.userUid)),
    [internalScheduleUsers, meetingDraft.hostUserUid, meetingDraft.internalAttendeeUids],
  );
  const hasUnsavedMeetingNoteChanges = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view') return false;
    const baseline = normalizeMeetingNoteDraft(toMeetingNoteDraft(activeMeetingNote ?? null));
    const current = normalizeMeetingNoteDraft(meetingNoteDraft);
    return JSON.stringify(baseline) !== JSON.stringify(current);
  }, [activeMeetingNote, meetingModal, meetingNoteDraft]);
  const hasUnsavedMeetingEventChanges = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view') return false;
    const baseline = normalizeMeetingEventDraft(toMeetingEventDraft(activeMeetingRecord));
    const current = normalizeMeetingEventDraft(meetingEventDraft);
    return JSON.stringify(baseline) !== JSON.stringify(current);
  }, [activeMeetingRecord, meetingEventDraft, meetingModal]);
  const canCreateDiscordMeeting = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord) return false;
    if (activeMeetingRecord.status !== 'scheduled') return false;
    const startsAt = new Date(activeMeetingRecord.startsAt);
    return !Number.isNaN(startsAt.getTime()) && startsAt.getTime() > Date.now();
  }, [activeMeetingRecord, meetingModal]);
  const isPendingMeetingRequest = activeMeetingRecord?.status === 'requested';
  const canGenerateMeetingInternalContext = useMemo(() => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord) return false;
    return Boolean(
      asText(meetingNoteDraft.summary)
      || asText(meetingNoteDraft.clientUpdates)
      || asText(meetingNoteDraft.decisions)
      || asText(meetingNoteDraft.nextSteps)
      || Object.values(meetingNoteDraft.onboardingProfile).some((value) => asText(value)),
    );
  }, [activeMeetingRecord, meetingModal, meetingNoteDraft]);
  const isOnboardingKickoffMeeting = activeMeetingRecord?.meetingType === 'intro_call';

  useEffect(() => {
    if (!meetingDraft.hostUserUid) return;
    if (!meetingDraft.internalAttendeeUids.includes(meetingDraft.hostUserUid)) return;
    setMeetingDraft((prev) => ({
      ...prev,
      internalAttendeeUids: prev.internalAttendeeUids.filter((value) => value !== prev.hostUserUid),
    }));
  }, [meetingDraft.hostUserUid, meetingDraft.internalAttendeeUids]);

  useEffect(() => {
    if (!meetingModal || meetingModal.mode !== 'view' || activeMeetingNote === undefined) return;
    setMeetingNoteDraft(toMeetingNoteDraft(activeMeetingNote));
    setMeetingNoteError(null);
    setMeetingInternalContextError(null);
  }, [activeMeetingNote, meetingModal]);

  useEffect(() => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord) return;
    setMeetingEventDraft(toMeetingEventDraft(activeMeetingRecord));
    setMeetingEventError(null);
    setMeetingDiscordError(null);
    setMeetingDiscordSyncing(false);
    setMeetingDiscordRemoving(false);
  }, [activeMeetingRecord, meetingModal]);

  useEffect(() => {
    if (!meetingModal || meetingModal.mode !== 'view') return;
    if (activeMeetingNote !== undefined) {
      setMeetingNoteLoading(false);
      return;
    }
    const eventId = meetingModal.meetingId;
    const requestId = meetingNoteRequestIdRef.current + 1;
    meetingNoteRequestIdRef.current = requestId;
    setMeetingNoteLoading(true);
    setMeetingNoteError(null);
    setMeetingInternalContextError(null);
    setMeetingNoteDraft(createEmptyMeetingNoteDraft());
    void (async () => {
      try {
        const payload = await apiFetch<any>(`/api/schedule/events/${encodeURIComponent(eventId)}/note`);
        if (meetingNoteRequestIdRef.current !== requestId) return;
        const normalized = payload ? normalizeMeetingNote(payload) : null;
        setMeetingNotesByEventId((prev) => ({ ...prev, [eventId]: normalized }));
        setMeetingNoteDraft(toMeetingNoteDraft(normalized));
      } catch (e: any) {
        if (meetingNoteRequestIdRef.current !== requestId) return;
        setMeetingNoteError(e?.message || 'Failed to load meeting notes');
        setMeetingNoteDraft(createEmptyMeetingNoteDraft());
      } finally {
        if (meetingNoteRequestIdRef.current === requestId) {
          setMeetingNoteLoading(false);
        }
      }
    })();
  }, [activeMeetingNote, meetingModal]);

  const toggleSuspended = async () => {
    if (!details || suspendSaving || deleteSaving) return;
    const nextSuspended = !details.isSuspended;
    const confirmed = window.confirm(
      nextSuspended
        ? 'Suspend this organization? Members will remain listed but operations can be restricted.'
        : 'Unsuspend this organization?',
    );
    if (!confirmed) return;
    try {
      setSuspendSaving(true);
      const payload = await apiFetch<any>(`/api/admin/organizations/${encodeURIComponent(details.id)}/suspend`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suspended: nextSuspended }),
      });
      const updated = normalizeOrganizationDetails(payload);
      if (!updated) return;
      setDetailsCache((prev) => ({ ...prev, [updated.id]: updated }));
      setRows((prev) => prev.map((row) => row.id === updated.id ? { ...row, isSuspended: updated.isSuspended } : row));
      setOrgActionsOpen(false);
    } catch (e: any) {
      window.alert(e?.message || 'Failed to update organization suspension');
    } finally {
      setSuspendSaving(false);
    }
  };

  const deleteOrganization = async () => {
    if (!details || deleteSaving || suspendSaving) return;
    const confirmed = window.confirm('Delete this organization? This cannot be undone.');
    if (!confirmed) return;
    try {
      setDeleteSaving(true);
      await apiFetch(`/api/admin/organizations/${encodeURIComponent(details.id)}`, {
        method: 'DELETE',
      });
      const nextRows = rows.filter((row) => row.id !== details.id);
      setRows(nextRows);
      setDetailsCache((prev) => {
        const next = { ...prev };
        delete next[details.id];
        return next;
      });
      setSelectedOrgId((current) => (current === details.id ? nextRows[0]?.id ?? null : current));
      setWorkspaceTab('overview');
      setOrgActionsOpen(false);
    } catch (e: any) {
      window.alert(e?.message || 'Failed to delete organization');
    } finally {
      setDeleteSaving(false);
    }
  };

  const ensureInternalScheduleUsers = async (): Promise<InternalScheduleUser[]> => {
    if (internalScheduleUsers.length) return internalScheduleUsers;
    if (internalScheduleUsersLoading) return internalScheduleUsers;
    setInternalScheduleUsersLoading(true);
    try {
      const loadUsersForRole = async (role: 'admin' | 'agent') => {
        const collected: InternalScheduleUser[] = [];
        let page = 0;
        while (page < 10) {
          const payload = await apiFetch<any>(`/api/admin/users?role=${role}&page=${page}`);
          const users = Array.isArray(payload?.users) ? payload.users : [];
          const normalized = users
            .map((entry: unknown) => {
              if (!entry || typeof entry !== 'object') return null;
              const record = entry as Record<string, unknown>;
              const uid = asText(record.id);
              if (!uid) return null;
              return {
                userUid: uid,
                name: asText(record.name) || emailLocalPart(asText(record.email)),
                email: asText(record.email) || null,
                photoURL: asText(record.photoURL) || asText(record.photoUrl) || asText(record.workspaceAvatarURL) || null,
                role,
              } satisfies InternalScheduleUser;
            })
            .filter((entry: InternalScheduleUser | null): entry is InternalScheduleUser => Boolean(entry));
          collected.push(...normalized);
          if (users.length < 20) break;
          page += 1;
        }
        return collected;
      };

      const [admins, agents] = await Promise.all([loadUsersForRole('admin'), loadUsersForRole('agent')]);
      const deduped = Array.from(new Map([...admins, ...agents].map((entry) => [entry.userUid, entry])).values())
        .sort((a, b) => a.name.localeCompare(b.name));
      setInternalScheduleUsers(deduped);
      return deduped;
    } catch (e) {
      console.error('Failed to load internal schedule users', e);
      return [];
    } finally {
      setInternalScheduleUsersLoading(false);
    }
  };

  const closeMeetingModal = () => {
    meetingNoteRequestIdRef.current += 1;
    setMeetingModal(null);
    setMeetingFormError(null);
    setMeetingDraft(createEmptyMeetingDraft(user?.uid ?? ''));
    setMeetingSaving(false);
    setMeetingEventDraft(createEmptyMeetingEventDraft());
    setMeetingEventSaving(false);
    setMeetingEventError(null);
    setMeetingNoteDraft(createEmptyMeetingNoteDraft());
    setMeetingNoteLoading(false);
    setMeetingNoteSaving(false);
    setMeetingNoteError(null);
    setMeetingInternalContextGenerating(false);
    setMeetingInternalContextError(null);
    setOrgAttendeePickerOpen(false);
    setInternalAttendeePickerOpen(false);
  };

  const openCreateMeetingModal = async (orgId: string, orgName: string) => {
    const scheduleUsers = await ensureInternalScheduleUsers();
    const orgDetails = details?.id === orgId ? details : detailsCache[orgId] ?? null;
    const defaultServiceContractId =
      orgDetails?.serviceContracts.length === 1
        ? orgDetails.serviceContracts[0].id
        : selectedContract && orgDetails?.serviceContracts.some((contract) => contract.id === selectedContract.id)
          ? selectedContract.id
          : '';
    const defaultOrgMemberUserIds = orgDetails?.primaryAccountHolder?.userId
      ? [orgDetails.primaryAccountHolder.userId]
      : orgDetails?.members[0]?.userId
        ? [orgDetails.members[0].userId]
        : [];
    setMeetingDraft({
      ...createEmptyMeetingDraft(user?.uid ?? scheduleUsers[0]?.userUid ?? ''),
      serviceContractId: defaultServiceContractId,
      orgMemberUserIds: defaultOrgMemberUserIds,
    });
    setMeetingFormError(null);
    setOrgAttendeePickerOpen(false);
    setInternalAttendeePickerOpen(false);
    setMeetingModal({ mode: 'create', orgId, orgName });
  };

  const openMeetingView = (orgId: string, orgName: string, meetingId: string) => {
    setMeetingFormError(null);
    setMeetingNoteError(null);
    setMeetingModal({ mode: 'view', orgId, orgName, meetingId });
    if (!internalScheduleUsers.length) {
      void ensureInternalScheduleUsers();
    }
  };

  const canDeleteMeeting = useCallback((meeting: OrganizationScheduledMeeting) => {
    if (meeting.status !== 'scheduled' && meeting.status !== 'requested') return false;
    const startsAt = new Date(meeting.startsAt).getTime();
    if (!Number.isFinite(startsAt)) return false;
    return startsAt > Date.now();
  }, []);

  const handleDeleteMeeting = useCallback(async (orgId: string, meeting: OrganizationScheduledMeeting) => {
    if (!canDeleteMeeting(meeting) || meetingDeletePendingById[meeting.id]) return;
    try {
      setMeetingDeletePendingById((prev) => ({ ...prev, [meeting.id]: true }));
      setMeetingErrorsByOrgId((prev) => ({ ...prev, [orgId]: null }));
      await apiFetch(`/api/schedule/events/${encodeURIComponent(meeting.id)}`, {
        method: 'DELETE',
      });
      setMeetingsByOrgId((prev) => ({
        ...prev,
        [orgId]: (prev[orgId] ?? []).filter((entry) => entry.id !== meeting.id),
      }));
      setMeetingNotesByEventId((prev) => {
        if (!(meeting.id in prev)) return prev;
        const next = { ...prev };
        delete next[meeting.id];
        return next;
      });
      setMeetingModal((current) => (
        current?.mode === 'view' && current.meetingId === meeting.id ? null : current
      ));
    } catch (e: any) {
      setMeetingErrorsByOrgId((prev) => ({
        ...prev,
        [orgId]: e?.message || 'Failed to delete meeting',
      }));
    } finally {
      setMeetingDeletePendingById((prev) => {
        const next = { ...prev };
        delete next[meeting.id];
        return next;
      });
    }
  }, [canDeleteMeeting, meetingDeletePendingById]);

  const openMeetingFromAiAgent = async (orgId: string, orgName: string, meetingId: string) => {
    if (!meetingsByOrgId[orgId]) {
      setMeetingsLoading((prev) => ({ ...prev, [orgId]: true }));
      setMeetingErrorsByOrgId((prev) => ({ ...prev, [orgId]: null }));
      try {
        await loadMeetingsForOrg(orgId);
      } catch (e: any) {
        setMeetingErrorsByOrgId((prev) => ({
          ...prev,
          [orgId]: e?.message || 'Failed to load meetings',
        }));
        return;
      } finally {
        setMeetingsLoading((prev) => ({ ...prev, [orgId]: false }));
      }
    }
    openMeetingView(orgId, orgName, meetingId);
  };

  const saveMeetingDraft = async () => {
    if (!meetingModal || meetingModal.mode !== 'create' || meetingSaving) return;
    if (!meetingDraft.hostUserUid) {
      setMeetingFormError('Host is required.');
      return;
    }
    if (!meetingDraft.meetingType) {
      setMeetingFormError('Meeting type is required.');
      return;
    }
    if (!asText(meetingDraft.startsAtLocal)) {
      setMeetingFormError('Meeting date and time are required.');
      return;
    }
    if (meetingDraft.orgMemberUserIds.length === 0) {
      setMeetingFormError('Select at least one client organization member.');
      return;
    }

    const orgDetails = details?.id === meetingModal.orgId ? details : detailsCache[meetingModal.orgId] ?? null;
    if ((orgDetails?.serviceContracts.length ?? 0) > 0 && !asText(meetingDraft.serviceContractId)) {
      setMeetingFormError('Select the service contract this meeting belongs to.');
      return;
    }
    const selectedOrgMembers = (orgDetails?.members ?? []).filter((member) =>
      meetingDraft.orgMemberUserIds.includes(member.userId),
    );
    if (selectedOrgMembers.length === 0) {
      setMeetingFormError('Select at least one valid client organization member.');
      return;
    }

    try {
      setMeetingSaving(true);
      setMeetingFormError(null);
      const startsAt = new Date(meetingDraft.startsAtLocal);
      if (Number.isNaN(startsAt.getTime())) {
        throw new Error('Meeting date and time are invalid.');
      }
      const selectedClientMember = selectedOrgMembers[0];
      const selectedInternalUsers = internalScheduleUsers.filter((entry) =>
        meetingDraft.internalAttendeeUids.includes(entry.userUid),
      );
      const normalizedMeetingLinks = normalizeMeetingLinkDrafts(meetingDraft.meetingLinks);
      const payload = {
        orgId: meetingModal.orgId,
        serviceContractId: asText(meetingDraft.serviceContractId) || undefined,
        hostUserUid: meetingDraft.hostUserUid,
        clientUserUid: selectedClientMember.uid || undefined,
        meetingType: meetingDraft.meetingType,
        startsAt: startsAt.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        durationMinutes: meetingDraft.durationMinutes,
        title: asText(meetingDraft.title) || undefined,
        description: asText(meetingDraft.description) || undefined,
        meetingLinks: normalizedMeetingLinks.filter(Boolean),
        attendees: [
          ...selectedOrgMembers.slice(1).map((member) => ({
            userId: member.userId,
            email: member.email || undefined,
            name: member.displayName || undefined,
            role: 'client',
          })),
          ...selectedInternalUsers
            .filter((entry) => entry.userUid !== meetingDraft.hostUserUid)
            .map((entry) => ({
              userUid: entry.userUid,
              email: entry.email || undefined,
              name: entry.name || undefined,
              role: entry.role,
            })),
        ],
      };
      const response = await apiFetch<any>('/api/schedule/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const created = normalizeScheduleMeeting(response);
      if (!created) throw new Error('Failed to create meeting');
      const normalized = meetingDraft.createDiscordMeeting
        ? normalizeScheduleMeeting(await apiFetch<any>(
            `/api/schedule/events/${encodeURIComponent(created.id)}/discord-meeting`,
            { method: 'POST' },
          )) ?? created
        : created;
      setMeetingsByOrgId((prev) => {
        const existing = prev[meetingModal.orgId] ?? [];
        return {
          ...prev,
          [meetingModal.orgId]: [normalized, ...existing]
            .filter((meeting, index, all) => all.findIndex((entry) => entry.id === meeting.id) === index)
            .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
        };
      });
      setMeetingErrorsByOrgId((prev) => ({ ...prev, [meetingModal.orgId]: null }));
      closeMeetingModal();
    } catch (e: any) {
      setMeetingFormError(e?.message || 'Failed to schedule meeting');
    } finally {
      setMeetingSaving(false);
    }
  };

  const saveMeetingEventDraft = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord || meetingEventSaving) return true;
    const meetingOrgDetails = details?.id === meetingModal.orgId ? details : detailsCache[meetingModal.orgId] ?? null;
    const normalizedDraft = normalizeMeetingEventDraft(meetingEventDraft);
    if ((meetingOrgDetails?.serviceContracts.length ?? 0) > 0 && !asText(normalizedDraft.serviceContractId)) {
      setMeetingEventError('Select the service contract this meeting belongs to.');
      return false;
    }
    try {
      setMeetingEventSaving(true);
      setMeetingEventError(null);
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: normalizedDraft.title || undefined,
            description: normalizedDraft.description || undefined,
            serviceContractId: normalizedDraft.serviceContractId || undefined,
            meetingLinks: normalizedDraft.meetingLinks.filter(Boolean),
          }),
        },
      );
      const normalized = normalizeScheduleMeeting(response);
      if (!normalized) throw new Error('Failed to save meeting details');
      setMeetingsByOrgId((prev) => {
        const existing = prev[meetingModal.orgId] ?? [];
        return {
          ...prev,
          [meetingModal.orgId]: existing
            .map((meeting) => (meeting.id === normalized.id ? normalized : meeting))
            .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
        };
      });
      setMeetingEventDraft(toMeetingEventDraft(normalized));
      return true;
    } catch (e: any) {
      setMeetingEventError(e?.message || 'Failed to save meeting details');
      return false;
    } finally {
      setMeetingEventSaving(false);
    }
  };

  const confirmPendingMeetingRequest = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord || !isPendingMeetingRequest || meetingEventSaving) return;
    const meetingOrgDetails = details?.id === meetingModal.orgId ? details : detailsCache[meetingModal.orgId] ?? null;
    const normalizedDraft = normalizeMeetingEventDraft(meetingEventDraft);
    if (!normalizedDraft.hostUserUid) {
      setMeetingEventError('Select the internal host who will own this meeting.');
      return;
    }
    if (!asText(normalizedDraft.startsAtLocal)) {
      setMeetingEventError('Meeting date and time are required.');
      return;
    }
    if ((meetingOrgDetails?.serviceContracts.length ?? 0) > 0 && !asText(normalizedDraft.serviceContractId)) {
      setMeetingEventError('Select the service contract this meeting belongs to.');
      return;
    }

    try {
      setMeetingEventSaving(true);
      setMeetingEventError(null);
      const startsAt = new Date(normalizedDraft.startsAtLocal);
      if (Number.isNaN(startsAt.getTime())) {
        throw new Error('Meeting date and time are invalid.');
      }
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}/confirm-request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hostUserUid: normalizedDraft.hostUserUid,
            meetingType: normalizedDraft.meetingType,
            serviceContractId: normalizedDraft.serviceContractId || undefined,
            startsAt: startsAt.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            durationMinutes: normalizedDraft.durationMinutes,
            title: normalizedDraft.title || undefined,
            description: normalizedDraft.description || undefined,
            meetingLinks: normalizedDraft.meetingLinks.filter(Boolean),
            createDiscordMeeting: normalizedDraft.createDiscordMeeting,
          }),
        },
      );
      const normalized = normalizeScheduleMeeting(response);
      if (!normalized) throw new Error('Failed to confirm meeting request');
      setMeetingsByOrgId((prev) => {
        const existing = prev[meetingModal.orgId] ?? [];
        return {
          ...prev,
          [meetingModal.orgId]: existing
            .map((meeting) => (meeting.id === normalized.id ? normalized : meeting))
            .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
        };
      });
      setMeetingEventDraft(toMeetingEventDraft(normalized));
    } catch (e: any) {
      setMeetingEventError(e?.message || 'Failed to confirm meeting request');
    } finally {
      setMeetingEventSaving(false);
    }
  };

  const createDiscordMeetingForActiveEvent = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord || meetingDiscordSyncing) return;
    if (!canCreateDiscordMeeting) {
      setMeetingDiscordError('Only upcoming scheduled meetings can have a Discord meeting added.');
      return;
    }

    const eventOk = hasUnsavedMeetingEventChanges ? await saveMeetingEventDraft() : true;
    if (!eventOk) return;

    try {
      setMeetingDiscordSyncing(true);
      setMeetingDiscordError(null);
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}/discord-meeting`,
        {
          method: 'POST',
        },
      );
      const normalized = normalizeScheduleMeeting(response);
      if (!normalized) throw new Error('Failed to create Discord meeting');
      setMeetingsByOrgId((prev) => {
        const existing = prev[meetingModal.orgId] ?? [];
        return {
          ...prev,
          [meetingModal.orgId]: existing
            .map((meeting) => (meeting.id === normalized.id ? normalized : meeting))
            .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
        };
      });
      setMeetingEventDraft(toMeetingEventDraft(normalized));
    } catch (e: any) {
      setMeetingDiscordError(e?.message || 'Failed to create Discord meeting');
    } finally {
      setMeetingDiscordSyncing(false);
    }
  };

  const removeDiscordMeetingForActiveEvent = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || !activeMeetingRecord?.discordMeeting || meetingDiscordRemoving) return;
    try {
      setMeetingDiscordRemoving(true);
      setMeetingDiscordError(null);
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}/discord-meeting`,
        {
          method: 'DELETE',
        },
      );
      const normalized = normalizeScheduleMeeting(response);
      if (!normalized) throw new Error('Failed to remove Discord meeting');
      setMeetingsByOrgId((prev) => {
        const existing = prev[meetingModal.orgId] ?? [];
        return {
          ...prev,
          [meetingModal.orgId]: existing
            .map((meeting) => (meeting.id === normalized.id ? normalized : meeting))
            .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
        };
      });
      setMeetingEventDraft(toMeetingEventDraft(normalized));
    } catch (e: any) {
      setMeetingDiscordError(e?.message || 'Failed to remove Discord meeting');
    } finally {
      setMeetingDiscordRemoving(false);
    }
  };

  const saveMeetingNoteDraft = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || meetingNoteSaving) return true;
    try {
      setMeetingNoteSaving(true);
      setMeetingNoteError(null);
      const payload = normalizeMeetingNoteDraft(meetingNoteDraft);
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}/note`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            onboardingProfile: toOnboardingProfilePayload(payload.onboardingProfile),
          }),
        },
      );
      const normalized = response ? normalizeMeetingNote(response) : null;
      setMeetingNotesByEventId((prev) => ({ ...prev, [meetingModal.meetingId]: normalized }));
      setMeetingNoteDraft(toMeetingNoteDraft(normalized));
      return true;
    } catch (e: any) {
      setMeetingNoteError(e?.message || 'Failed to save meeting notes');
      return false;
    } finally {
      setMeetingNoteSaving(false);
    }
  };

  const saveMeetingChanges = async () => {
    if (!meetingModal || meetingModal.mode !== 'view') return;
    const eventOk = hasUnsavedMeetingEventChanges ? await saveMeetingEventDraft() : true;
    if (!eventOk) return;
    if (hasUnsavedMeetingNoteChanges) {
      await saveMeetingNoteDraft();
    }
  };

  const generateMeetingInternalContext = async () => {
    if (!meetingModal || meetingModal.mode !== 'view' || meetingInternalContextGenerating) return;
    if (!canGenerateMeetingInternalContext) {
      setMeetingInternalContextError('Add meeting notes or onboarding kickoff details before generating internal context.');
      return;
    }
    try {
      setMeetingInternalContextGenerating(true);
      setMeetingInternalContextError(null);
      const payload = normalizeMeetingNoteDraft(meetingNoteDraft);
      const response = await apiFetch<any>(
        `/api/schedule/events/${encodeURIComponent(meetingModal.meetingId)}/note/internal-context/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            summary: payload.summary,
            clientUpdates: payload.clientUpdates,
            decisions: payload.decisions,
            nextSteps: payload.nextSteps,
            onboardingProfile: toOnboardingProfilePayload(payload.onboardingProfile),
          }),
        },
      );
      const generated = asText(response?.internalContext);
      if (!generated) {
        throw new Error('The AI generator returned an empty internal context summary.');
      }
      setMeetingNoteDraft((prev) => ({ ...prev, internalContext: generated }));
    } catch (e: any) {
      setMeetingInternalContextError(e?.message || 'Failed to generate internal context');
    } finally {
      setMeetingInternalContextGenerating(false);
    }
  };

  return (
    <Page>
      <Header>
        <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 900, letterSpacing: '.28px', textTransform: 'uppercase' }}>Client Operations Tool</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, color: '#e8eaed', fontSize: 'clamp(1.5rem, 2.2vw, 2rem)', lineHeight: 1, fontWeight: 900 }}>Organizations</h1>
            <p style={{ margin: '6px 0 0', maxWidth: 980, color: '#9aa4b2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>Search and filter clients from the left rail, then evaluate each account through service utilization, account health, meetings, members, and control actions in the workspace.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <SmallButton type="button" $accent onClick={() => setCreateOrganizationOpen(true)}><Plus size={14} style={{ marginRight: 6 }} />Create Organization</SmallButton>
          </div>
        </div>
      </Header>

      <Workspace ref={controlsRef}>
        <Panel style={{ display: 'grid', gridTemplateRows: 'auto auto minmax(0,1fr) auto' }}>
          <div style={{ padding: 12, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
            <div style={{ margin: 0, color: '#e8eaed', fontSize: 12, fontWeight: 900, letterSpacing: '.3px', textTransform: 'uppercase' }}>Organizations</div>
            <p style={{ margin: '2px 0 0', color: '#9aa4b2', fontSize: 11, lineHeight: 1.45, fontWeight: 700 }}>Searchable client directory with the same filter-first workflow used in KBase.</p>
          </div>
          <div style={{ padding: '10px 12px', display: 'grid', gap: 8, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8 }}>
              <label style={{ position: 'relative', display: 'block' }}>
                <Search size={16} style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', color: 'rgba(255,255,255,.38)' }} />
                <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search organizations" />
              </label>
              <div style={{ position: 'relative' }}>
                <SmallButton type="button" onClick={() => setFiltersOpen((value) => !value)} style={{ minWidth: 38, width: 38, padding: 0, position: 'relative' }}><Filter size={15} /></SmallButton>
                {filterCount > 0 ? <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'rgba(224,161,94,.96)', color: '#111', fontSize: 10, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{filterCount}</span> : null}
                {filtersOpen ? <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 280, zIndex: 20, border: '1px solid rgba(255,255,255,.14)', borderRadius: 14, background: '#171b23', boxShadow: '0 24px 60px rgba(0,0,0,.45)', padding: 12, display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={labelStyle}>Service State</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{(['all', 'active', 'inactive'] as const).map((value) => <button key={value} type="button" onClick={() => setServiceStateFilter(value)} style={{ minHeight: 30, padding: '0 10px', borderRadius: 999, border: serviceStateFilter === value ? '1px solid rgba(224,161,94,.34)' : '1px solid rgba(255,255,255,.14)', background: serviceStateFilter === value ? 'rgba(224,161,94,.12)' : 'rgba(255,255,255,.03)', color: '#f3f4f6', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>{value === 'all' ? 'All' : value}</button>)}</div>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={labelStyle}>Team Assignment</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{([['all', 'All'], ['with_team', 'With team'], ['without_team', 'Without team']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => setTeamFilter(value)} style={{ minHeight: 30, padding: '0 10px', borderRadius: 999, border: teamFilter === value ? '1px solid rgba(224,161,94,.34)' : '1px solid rgba(255,255,255,.14)', background: teamFilter === value ? 'rgba(224,161,94,.12)' : 'rgba(255,255,255,.03)', color: '#f3f4f6', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>{label}</button>)}</div>
                  </div>
                </div> : null}
              </div>
            </div>
          </div>
          <div style={{ padding: '8px 12px 12px', display: 'grid', gap: 8, minHeight: 0, overflow: 'auto', alignContent: 'start' }}>
            {loading ? <EmptyState>Loading organizations...</EmptyState> : null}
            {!loading && error ? <EmptyState>{error}</EmptyState> : null}
            {!loading && !error && filteredRows.length === 0 ? <EmptyState>No organizations match the current search/filter state.</EmptyState> : null}
            {!loading && !error ? visibleRows.map((row) => <button key={row.id} type="button" onClick={() => setSelectedOrgId(row.id)} style={{ width: '100%', textAlign: 'left', borderRadius: 12, border: row.id === selectedOrgId ? '1px solid rgba(224,161,94,.32)' : '1px solid rgba(255,255,255,.08)', background: row.id === selectedOrgId ? 'rgba(224,161,94,.1)' : 'rgba(255,255,255,.02)', color: '#e8eaed', padding: 10, display: 'grid', gap: 10, cursor: 'pointer' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', gap: 10, alignItems: 'center' }}>
                <PortalAvatar
                  size={34}
                  photoURL={row.logoURL}
                  name={row.name}
                  disableNavigation
                  implicitSelfFallback={false}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, lineHeight: 1.2 }}>{row.name}</div>
                  <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, lineHeight: 1.35 }}>{row.ownerDisplay} - {row.memberCount} members</div>
                </div>
                <StatusPill $tone={row.isSuspended ? 'bad' : row.primaryServiceActive === true ? 'good' : row.primaryServiceActive === false ? 'warn' : 'neutral'}>{row.isSuspended ? 'Suspended' : row.primarySubscriptionStatus || 'No plan'}</StatusPill>
              </div>
              <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{row.assignedServiceTeam ? `Assigned to ${row.assignedServiceTeam}` : 'No service team assigned'}</div>
            </button>) : null}
          </div>
          <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderTop: '1px solid rgba(255,255,255,.08)' }}>
            <div style={{ color: 'rgba(218,222,231,.58)', fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
              Page {organizationPage} of {organizationTotalPages}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SmallButton type="button" onClick={() => setOrganizationPage((current) => Math.max(1, current - 1))} disabled={organizationPage === 1}>
                Prev
              </SmallButton>
              <SmallButton
                type="button"
                onClick={() => setOrganizationPage((current) => (current < organizationTotalPages ? current + 1 : current))}
                disabled={organizationPage >= organizationTotalPages}
              >
                Next
              </SmallButton>
            </div>
          </div>
        </Panel>
        <Panel style={{ display: 'grid', gridTemplateRows: 'auto auto minmax(0,1fr)' }}>
          <div style={{ padding: '14px 16px 10px', display: 'grid', gap: 12, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
            {selectedRow ? <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, color: '#e8eaed', fontSize: '1.35rem', fontWeight: 900 }}>{selectedRow.name}</h2>
                </div>
                <p style={{ margin: 0, color: '#9aa4b2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>{details?.organizationType === 'business' ? details.businessName || 'Business organization' : 'Individual client'} - {details?.ownerEmail || selectedRow.ownerEmail} - {details?.assignedServiceTeams.join(', ') || selectedRow.assignedServiceTeam || 'No team assigned'}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginLeft: 'auto' }}>
                <StatusPill $tone={selectedRow.isSuspended ? 'bad' : selectedRow.primaryServiceActive === true ? 'good' : 'neutral'}>{selectedRow.isSuspended ? 'Suspended' : 'Client active'}</StatusPill>
                {role === 'admin' ? <HeaderActionsWrap><OverflowMenuAnchor ref={orgActionsRef}><OverflowIconButton type="button" onClick={() => setOrgActionsOpen((value) => !value)} aria-haspopup="menu" aria-expanded={orgActionsOpen} aria-label="Organization actions"><MoreHorizontal size={16} /></OverflowIconButton>{orgActionsOpen ? <OverflowMenu role="menu" aria-label="Organization actions"><OverflowMenuItem type="button" onClick={() => void toggleSuspended()} disabled={suspendSaving || deleteSaving}><ShieldAlert size={14} />{suspendSaving ? 'Saving...' : details?.isSuspended ? 'Unsuspend organization' : 'Suspend organization'}</OverflowMenuItem><OverflowMenuItem type="button" $danger onClick={() => void deleteOrganization()} disabled={deleteSaving || suspendSaving}><Trash2 size={14} />{deleteSaving ? 'Deleting...' : 'Delete organization'}</OverflowMenuItem></OverflowMenu> : null}</OverflowMenuAnchor></HeaderActionsWrap> : null}
              </div>
            </div> : <EmptyState>Select an organization from the left rail to begin.</EmptyState>}
          </div>
          <div style={{ padding: '8px 16px 0', display: 'flex', gap: 6, overflow: 'auto', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
            {([['overview', 'Overview', Building2], ['meetings', 'Meetings', CalendarDays], ['members', 'Members', Users], ['services', 'Services', Plus], ['ai_agent', 'AI Agent', Bot]] as const).map(([key, label, Icon]) => <button key={key} type="button" onClick={() => setWorkspaceTab(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 12px', borderRadius: '10px 10px 0 0', borderTop: workspaceTab === key ? '1px solid rgba(224,161,94,.34)' : '1px solid rgba(255,255,255,.08)', borderLeft: workspaceTab === key ? '1px solid rgba(224,161,94,.34)' : '1px solid rgba(255,255,255,.08)', borderRight: workspaceTab === key ? '1px solid rgba(224,161,94,.34)' : '1px solid rgba(255,255,255,.08)', borderBottom: '1px solid transparent', background: workspaceTab === key ? 'rgba(224,161,94,.1)' : 'rgba(255,255,255,.02)', color: '#e8eaed', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}><Icon size={14} />{label}</button>)}
          </div>
          <div
            ref={workspaceBodyRef}
            style={{
              minHeight: 0,
              height: '100%',
              overflowY: workspaceTab === 'ai_agent' ? 'hidden' : 'auto',
              overflowX: 'hidden',
              padding: workspaceTab === 'ai_agent' ? 0 : '14px 16px 18px',
              boxSizing: 'border-box',
              display: 'grid',
              gap: 14,
              alignContent: workspaceTab === 'ai_agent' ? 'stretch' : 'start',
            }}
          >
            {selectedOrgId && detailsLoading && !details ? <EmptyState>Loading organization workspace...</EmptyState> : null}
            {selectedOrgId && detailsError && !details ? <EmptyState>{detailsError}</EmptyState> : null}
            {details && workspaceTab === 'overview' ? <>
              <BaseCard style={{ padding: 14, display: 'grid', gap: 12 }}><div><div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, letterSpacing: '.22px', textTransform: 'uppercase' }}>Account Health</div><p style={{ margin: '4px 0 0', color: '#9aa4b2', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>Quick operating context for client serviceability and delivery planning.</p></div><div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                  <div style={cellStyle}><div style={labelStyle}>Primary Contact</div><div style={{ ...valueStyle, fontSize: 18 }}>{details.primaryAccountHolder?.displayName || details.ownerDisplay}</div></div>
                  <div style={cellStyle}><div style={labelStyle}>Email</div><div style={{ ...valueStyle, fontSize: 18 }}>{details.primaryAccountHolder?.email || details.ownerEmail || '--'}</div></div>
                  <div style={cellStyle}><div style={labelStyle}>Projects</div><div style={valueStyle}>{details.projectCount}</div></div>
                  <div style={cellStyle}><div style={labelStyle}>Tickets</div><div style={valueStyle}>{details.ticketCount}</div></div>
                  <div style={cellStyle}><div style={labelStyle}>Active Services</div><div style={valueStyle}>{details.accountHealth?.activeServiceCount ?? details.serviceContracts.length}</div></div>
                  <div style={cellStyle}><div style={labelStyle}>Outside Target</div><div style={valueStyle}>{details.accountHealth?.outsideTargetCount ?? 0}</div></div>
                </div></BaseCard>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, letterSpacing: '.22px', textTransform: 'uppercase' }}>Service Contract Snapshot</div>
                  <p style={{ margin: '4px 0 0', color: '#9aa4b2', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>Contract-by-contract scope, team ownership, monthly utilization, and delivery profile.</p>
                </div>
                {details.serviceContracts.length ? (
                  <ServiceContractSnapshotGrid>
                    {details.serviceContracts.map((contract) => {
                      const contractTeamLabels = getContractTeamLabels(details, contract);
                      const contractMinMinutes = contract.contractedMonthlyMinutesMin ?? 0;
                      const contractMaxMinutes = contract.contractedMonthlyMinutesMax ?? 0;
                      const contractConsumedMinutes = contract.consumedBillableMinutes ?? 0;
                      const contractFloorWidth = contractMaxMinutes > 0 ? Math.min(100, (contractMinMinutes / contractMaxMinutes) * 100) : 0;
                      const contractConsumedWidth = contractMaxMinutes > 0 ? Math.min(100, (contractConsumedMinutes / contractMaxMinutes) * 100) : 0;
                      const utilizationSummary = contractConsumedMinutes > contractMaxMinutes && contractMaxMinutes > 0
                        ? `Over max by ${formatMinutesAsHours(contractConsumedMinutes - contractMaxMinutes)}`
                        : contractMinMinutes > 0 && contractConsumedMinutes < contractMinMinutes
                          ? `${formatMinutesAsHours(contractMinMinutes - contractConsumedMinutes)} until floor`
                          : `Within buffer up to ${formatMinutesAsHours(contractMaxMinutes)}`;
                      return (
                        <BaseCard key={contract.id} style={{ padding: 14, display: 'grid', gap: 12, alignContent: 'start' }}>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>
                              {contract.serviceName || 'Service contract'}
                            </div>
                            <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>
                              {contract.tierName || 'No tier selected'}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ position: 'relative', height: 12, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,.06)' }}>
                              <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${contractFloorWidth}%`, background: 'rgba(224,161,94,.12)' }} />
                              <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${contractConsumedWidth}%`, background: contractConsumedMinutes > contractMaxMinutes && contractMaxMinutes > 0 ? 'rgba(255,110,110,.82)' : 'rgba(100,220,170,.88)' }} />
                              {contractMaxMinutes > 0 && contractMinMinutes > 0 ? <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${contractFloorWidth}%`, width: 2, background: 'rgba(255,255,255,.75)' }} /> : null}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>
                              <span>Measured {formatDateLabel(contract.usageMeasuredThroughAt)}</span>
                              <span>{utilizationSummary}</span>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                            <div style={cellStyle}><div style={labelStyle}>Status</div><div style={{ ...valueStyle, fontSize: 18 }}>{contract.status}</div></div>
                            <div style={cellStyle}><div style={labelStyle}>Contract Term</div><div style={{ ...valueStyle, fontSize: 18 }}>{contract.contractTerm || '--'}</div></div>
                            <div style={cellStyle}><div style={labelStyle}>Service Team</div><div style={{ ...valueStyle, fontSize: 18 }}>{contractTeamLabels.join(', ') || 'No team assigned'}</div></div>
                            <div style={cellStyle}><div style={labelStyle}>Delivery Range</div><div style={{ ...valueStyle, fontSize: 18 }}>{formatMinutesRange(contract.contractedMonthlyMinutesMin, contract.contractedMonthlyMinutesMax)}</div></div>
                            <div style={cellStyle}><div style={labelStyle}>This Month</div><div style={{ ...valueStyle, fontSize: 18 }}>{formatMinutesAsHours(contract.consumedBillableMinutes)}</div></div>
                            <div style={cellStyle}><div style={labelStyle}>Tracked Tickets</div><div style={{ ...valueStyle, fontSize: 18 }}>{contract.trackedTicketCount}</div></div>
                          </div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ color: '#9aa4b2', fontSize: 10, fontWeight: 900, letterSpacing: '.24px', textTransform: 'uppercase' }}>
                              Included Offerings
                            </div>
                            {contract.selectedServiceOfferings.length ? (
                              <ServiceContractOfferingsList>
                                {contract.selectedServiceOfferings.map((value) => (
                                  <div
                                    key={`${contract.id}:${value}`}
                                    style={{ color: '#f3f4f6', fontSize: 11, fontWeight: 800, lineHeight: 1.45 }}
                                  >
                                    {value}
                                  </div>
                                ))}
                              </ServiceContractOfferingsList>
                            ) : (
                              <span style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>No offerings selected</span>
                            )}
                          </div>
                        </BaseCard>
                      );
                    })}
                  </ServiceContractSnapshotGrid>
                ) : (
                  <EmptyState>No service contract data is available for this organization yet.</EmptyState>
                )}
              </div>
            </> : null}
            {details && workspaceTab === 'meetings' ? (
              <MeetingsWorkspaceCard>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'grid', gap: 10, minWidth: 0, flex: '1 1 620px' }}>
                    <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, letterSpacing: '.22px', textTransform: 'uppercase' }}>Meetings</div>
                    <p style={{ margin: '4px 0 0', color: '#9aa4b2', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>Schedule real client meetings and reopen any meeting card to review or update saved notes.</p>
                    <MeetingToolbar>
                      <MeetingToolbarSelect value={meetingServiceFilter} onChange={(event) => setMeetingServiceFilter(event.target.value as MeetingServiceFilter)} aria-label="Filter meetings by service">
                        <option value="all">All services</option>
                        {meetingServiceOptions.map((entry) => (
                          <option key={entry.id} value={entry.id}>{entry.label}</option>
                        ))}
                        <option value="unassigned">Unassigned contract</option>
                      </MeetingToolbarSelect>
                      <MeetingToolbarSelect value={meetingTypeFilter} onChange={(event) => setMeetingTypeFilter(event.target.value as MeetingTypeFilter)} aria-label="Filter meetings by meeting type">
                        <option value="all">All meeting types</option>
                        {meetingTypeOptions.map((value) => (
                          <option key={value} value={value}>{SCHEDULE_MEETING_TYPE_LABELS[value]}</option>
                        ))}
                      </MeetingToolbarSelect>
                      <MeetingToolbarSelect value={meetingStatusFilter} onChange={(event) => setMeetingStatusFilter(event.target.value as MeetingStatusFilter)} aria-label="Filter meetings by status">
                        <option value="all">All statuses</option>
                        <option value="requested">Requested</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="canceled">Canceled</option>
                        <option value="no_show">No show</option>
                      </MeetingToolbarSelect>
                      <MeetingToolbarSelect value={meetingSortOrder} onChange={(event) => setMeetingSortOrder(event.target.value as MeetingSortOrder)} aria-label="Sort meetings">
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                        <option value="soonest">Soonest first</option>
                      </MeetingToolbarSelect>
                    </MeetingToolbar>
                  </div>
                  <div style={{ display: 'grid', gap: 8, justifyItems: 'end', alignSelf: 'start' }}>
                    <MeetingInlineButton type="button" onClick={() => void openCreateMeetingModal(details.id, details.name)}>
                      <Plus size={14} />
                      Add meeting
                    </MeetingInlineButton>
                  </div>
                </div>
                <MeetingsScrollArea>
                  {meetingsLoading[selectedOrgId || ''] ? <EmptyState>Loading meetings...</EmptyState> : null}
                  {!meetingsLoading[selectedOrgId || ''] && meetingsError ? <EmptyState>{meetingsError}</EmptyState> : null}
                  {!meetingsLoading[selectedOrgId || ''] && !meetingsError && visibleMeetings.length === 0 ? <EmptyState>{hasActiveMeetingFilters ? 'No meetings match the current filters.' : 'No meetings found for this organization.'}</EmptyState> : null}
                {!meetingsLoading[selectedOrgId || ''] && !meetingsError && visibleMeetings.length > 0 ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {visibleMeetings.map((meeting) => {
                      const contract = meeting.serviceContractId ? serviceContractsById.get(meeting.serviceContractId) ?? null : null;
                      const meetingServiceLabel = contract?.serviceName || meeting.serviceName || contract?.tierName || meeting.serviceCode || null;
                      return (
                      <MeetingCardButton
                        as="div"
                        key={meeting.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openMeetingView(details.id, details.name, meeting.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openMeetingView(details.id, details.name, meeting.id);
                          }
                        }}
                      >
                        <MeetingCardContent>
                          <MeetingCardTitle>{meeting.title || SCHEDULE_MEETING_TYPE_LABELS[meeting.meetingType]}</MeetingCardTitle>
                          <MeetingCardMetaLine>Meeting type: {SCHEDULE_MEETING_TYPE_LABELS[meeting.meetingType]}</MeetingCardMetaLine>
                          <MeetingCardMetaLine $tone={meeting.serviceContractId ? 'default' : 'warn'}>
                            Service: {formatMeetingServiceLabel(meetingServiceLabel)}
                          </MeetingCardMetaLine>
                          <MeetingCardDetail>
                            {meeting.status === 'requested'
                              ? `Requested for ${formatRequestedMeetingDate(meeting.requestedForDate)}`
                              : `${formatMeetingDateRange(meeting.startsAt, meeting.endsAt)} - ${meeting.timezone}`}
                          </MeetingCardDetail>
                          <MeetingCardDetail>{buildMeetingParticipants(meeting)}</MeetingCardDetail>
                          <MeetingCardDetail>{buildMeetingSnippet(meeting)}</MeetingCardDetail>
                        </MeetingCardContent>
                        <MeetingCardActions>
                          <MeetingCardStatus $tone={meeting.status === 'requested' ? 'warn' : meeting.status === 'completed' ? 'good' : meeting.status === 'canceled' ? 'bad' : 'neutral'}>
                            {meeting.status === 'requested' ? 'request pending' : meeting.status}
                          </MeetingCardStatus>
                          {canDeleteMeeting(meeting) ? (
                            <MeetingDeleteIconButton
                              type="button"
                              aria-label="Delete meeting"
                              title="Delete meeting for everyone"
                              disabled={Boolean(meetingDeletePendingById[meeting.id])}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                void handleDeleteMeeting(details.id, meeting);
                              }}
                            >
                              <Trash2 size={14} />
                            </MeetingDeleteIconButton>
                          ) : null}
                        </MeetingCardActions>
                      </MeetingCardButton>
                    );})}
                  </div>
                ) : null}
                </MeetingsScrollArea>
              </MeetingsWorkspaceCard>
            ) : null}
            {details && workspaceTab === 'members' ? (
              <MembersWorkspaceCard>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, letterSpacing: '.22px', textTransform: 'uppercase' }}>Members</div>
                    <p style={{ margin: '4px 0 0', color: '#9aa4b2', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>Primary account holder and linked client members.</p>
                  </div>
                  <MeetingInlineButton type="button" onClick={() => setCreateClientOpen(true)}>
                    <Plus size={14} />
                    Add Member
                  </MeetingInlineButton>
                </div>
                <MembersScrollArea>
                  {details.members.length === 0 ? (
                    <EmptyState>No members recorded for this organization.</EmptyState>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {details.members.map((member) => (
                        <div key={member.uid || member.userId || member.email || member.displayName} style={cellStyle}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', gap: 10, alignItems: 'center' }}>
                            <PortalAvatar size={38} uid={member.uid || member.userId} email={member.email} photoURL={member.photoURL} name={member.displayName} disableNavigation implicitSelfFallback={false} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{member.displayName}</div>
                              <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{member.email || '--'} - {member.role}</div>
                            </div>
                            {member.isPrimaryAccountHolder ? <StatusPill $tone="good">Primary</StatusPill> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </MembersScrollArea>
              </MembersWorkspaceCard>
            ) : null}
            {details && workspaceTab === 'services' ? (
              <OrganizationServicesPanel
                orgId={details.id}
                orgName={details.name}
              />
            ) : null}
            {details && workspaceTab === 'ai_agent' ? (
              <div style={{ minHeight: 0, height: '100%' }}>
                <OrganizationAIAgentPanel
                  orgId={details.id}
                  orgName={details.name}
                  ownerEmail={details.ownerEmail || selectedRow?.ownerEmail || null}
                  primaryClientUid={details.primaryAccountHolder?.uid || details.members[0]?.uid || null}
                  serviceTab={serviceTab}
                  hasActiveServiceContract={Boolean(selectedContract)}
                  serviceContract={
                    selectedContract
                      ? {
                          id: selectedContract.id,
                          serviceCode: selectedContract.serviceCode,
                          serviceName: selectedContract.serviceName,
                          offerings: (
                            selectedContract.offeringEntitlements.length
                              ? selectedContract.offeringEntitlements
                              : selectedContract.selectedServiceOfferingCodes.map((offeringCode, index) => ({
                                  offeringCode,
                                  offeringName: selectedContract.selectedServiceOfferings[index] || offeringCode,
                                }))
                          ).map((offering) => ({
                            serviceContractId: selectedContract.id,
                            serviceCode: selectedContract.serviceCode,
                            serviceName: selectedContract.serviceName,
                            offeringCode: offering.offeringCode,
                            offeringName: offering.offeringName,
                          })),
                        }
                      : null
                  }
                  projectCount={details.projectCount}
                  onOpenMeeting={(meetingId) => {
                    void openMeetingFromAiAgent(details.id, details.name, meetingId);
                  }}
                />
              </div>
            ) : null}
          </div>
        </Panel>
      </Workspace>
      {createOrganizationOpen ? (
        <ModalOverlay onMouseDown={closeCreateOrganizationModal}>
          <ModalCard onMouseDown={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h3 style={{ margin: 0, color: '#f3f4f6', fontSize: 16, fontWeight: 900 }}>Create Organization</h3>
              <SmallButton type="button" onClick={closeCreateOrganizationModal} disabled={createOrganizationSaving}>
                <X size={14} />
              </SmallButton>
            </div>
            <p style={{ margin: 0, color: '#9aa4b2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>
              Create a business organization and optionally invite a primary account holder who can accept ownership and pay for services.
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ ...createOrganizationSectionLabelStyle, color: '#f3f4f6' }}>Required</div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Business name *</span>
                  <input
                    style={createOrganizationInputStyle}
                    value={createOrganizationDraft.businessName}
                    onChange={(event) => setCreateOrganizationDraft((prev) => ({ ...prev, businessName: event.target.value }))}
                    placeholder="Business name"
                    disabled={createOrganizationSaving}
                  />
                </label>
                <div style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Industry *</span>
                  <CreateOrganizationSelectWrap ref={createOrganizationIndustryRef}>
                    <CreateOrganizationSelectButton
                      type="button"
                      $empty={!createOrganizationDraft.industry}
                      onClick={() => setCreateOrganizationIndustryOpen((current) => !current)}
                      disabled={createOrganizationSaving}
                      aria-haspopup="listbox"
                      aria-expanded={createOrganizationIndustryOpen}
                    >
                      <span>{createOrganizationDraft.industry || 'Select industry'}</span>
                      <ChevronDown size={15} />
                    </CreateOrganizationSelectButton>
                    {createOrganizationIndustryOpen ? (
                      <CreateOrganizationSelectMenu role="listbox" aria-label="Industry">
                        {INDUSTRY_OPTIONS.map((industry) => (
                          <CreateOrganizationSelectOption
                            key={industry}
                            type="button"
                            $active={createOrganizationDraft.industry === industry}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              setCreateOrganizationDraft((prev) => ({ ...prev, industry }));
                              setCreateOrganizationIndustryOpen(false);
                            }}
                          >
                            {industry}
                          </CreateOrganizationSelectOption>
                        ))}
                      </CreateOrganizationSelectMenu>
                    ) : null}
                  </CreateOrganizationSelectWrap>
                </div>
              </div>
              <div style={createOrganizationSectionLabelStyle}>Optional</div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Business phone</span>
                  <input
                    style={createOrganizationInputStyle}
                    value={createOrganizationDraft.contactPhone}
                    onChange={(event) => setCreateOrganizationDraft((prev) => ({ ...prev, contactPhone: event.target.value }))}
                    placeholder="Business phone"
                    disabled={createOrganizationSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Website</span>
                  <input
                    style={createOrganizationInputStyle}
                    value={createOrganizationDraft.website}
                    onChange={(event) => setCreateOrganizationDraft((prev) => ({ ...prev, website: event.target.value }))}
                    placeholder="Website"
                    disabled={createOrganizationSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
                  <span style={createOrganizationFieldLabelStyle}>Address</span>
                  <input
                    style={createOrganizationInputStyle}
                    value={createOrganizationDraft.address}
                    onChange={(event) => setCreateOrganizationDraft((prev) => ({ ...prev, address: event.target.value }))}
                    placeholder="Address"
                    disabled={createOrganizationSaving}
                  />
                </label>
              </div>
            </div>
            {createOrganizationError ? (
              <div style={{ color: '#fca5a5', fontSize: 12, fontWeight: 700 }}>{createOrganizationError}</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <SmallButton type="button" onClick={closeCreateOrganizationModal} disabled={createOrganizationSaving}>Close</SmallButton>
              <SmallButton type="button" $accent onClick={() => void submitCreateOrganization()} disabled={createOrganizationSaving}>
                {createOrganizationSaving ? 'Creating...' : 'Create Organization'}
              </SmallButton>
            </div>
          </ModalCard>
        </ModalOverlay>
      ) : null}
      {createClientOpen ? (
        <ModalOverlay onMouseDown={closeAddMemberModal}>
          <ModalCard onMouseDown={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h3 style={{ margin: 0, color: '#f3f4f6', fontSize: 16, fontWeight: 900 }}>Add Member</h3>
              <SmallButton type="button" onClick={closeAddMemberModal} disabled={memberInviteSaving}>
                <X size={14} />
              </SmallButton>
            </div>
            <p style={{ margin: 0, color: '#9aa4b2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>
              Search existing platform clients to invite them into this organization, or enter any email directly to invite someone who is not on the platform yet.
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <span style={createOrganizationFieldLabelStyle}>Search platform clients</span>
                <MemberInviteSearchWrap ref={memberInviteSearchRef}>
                  <input
                    style={createOrganizationInputStyle}
                    value={memberInviteQuery}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setMemberInviteQuery(nextValue);
                      setMemberInviteSearchOpen(nextValue.trim().length >= 2);
                    }}
                    onFocus={() => {
                      if (memberInviteQuery.trim().length >= 2) {
                        setMemberInviteSearchOpen(true);
                      }
                    }}
                    placeholder="Search by client name or email"
                    disabled={memberInviteSaving}
                  />
                  {memberInviteSearchOpen && memberInviteQuery.trim().length >= 2 ? (
                    <CreateOrganizationSelectMenu role="listbox" aria-label="Matching platform clients">
                      {memberInviteLoading ? (
                        <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 800, padding: '4px 6px' }}>
                          Searching platform clients...
                        </div>
                      ) : memberInviteResults.length ? (
                        memberInviteResults.map((client) => (
                          <CreateOrganizationSelectOption
                            key={`${client.uid}:${client.email}`}
                            type="button"
                            $active={memberInviteEmail.trim().toLowerCase() === client.email.trim().toLowerCase()}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              setMemberInviteEmail(client.email);
                              setMemberInviteQuery(client.name || client.email);
                              setMemberInviteSearchOpen(false);
                            }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: 10, alignItems: 'center' }}>
                              <PortalAvatar
                                size={30}
                                uid={client.uid || undefined}
                                email={client.email}
                                photoURL={client.photoURL}
                                name={client.name || client.email}
                                disableNavigation
                                implicitSelfFallback={false}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 800 }}>{client.name || client.email}</div>
                                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, lineHeight: 1.45 }}>
                                  {client.email}{client.businessName ? ` - ${client.businessName}` : ''}
                                </div>
                              </div>
                            </div>
                          </CreateOrganizationSelectOption>
                        ))
                      ) : (
                        <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, padding: '4px 6px' }}>
                          No matching platform clients found.
                        </div>
                      )}
                    </CreateOrganizationSelectMenu>
                  ) : null}
                </MemberInviteSearchWrap>
              </div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0,1fr) 180px', alignItems: 'end' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Invite email *</span>
                  <input
                    style={createOrganizationInputStyle}
                    value={memberInviteEmail}
                    onChange={(event) => setMemberInviteEmail(event.target.value)}
                    placeholder="name@business.com"
                    disabled={memberInviteSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={createOrganizationFieldLabelStyle}>Membership type *</span>
                  <MemberInviteSelect
                    value={memberInviteRole}
                    onChange={(event) => setMemberInviteRole(event.target.value as MemberInviteRole)}
                    disabled={memberInviteSaving}
                  >
                    {!organizationHasPrimaryAccountHolder ? <option value="owner">Primary account holder</option> : null}
                    <option value="member">Member</option>
                  </MemberInviteSelect>
                </label>
              </div>
              <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, lineHeight: 1.5 }}>
                {organizationHasPrimaryAccountHolder
                  ? `This organization already has a primary account holder${organizationPrimaryAccountHolderLabel ? `: ${organizationPrimaryAccountHolderLabel}` : ''}. Additional invites can only be sent as members.`
                  : 'This organization does not have a primary account holder yet, so you can invite one now or add a regular member.'}
              </div>
              <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, lineHeight: 1.5 }}>
                If the email already belongs to a platform client, they will receive an org invite. If not, the same invite email can be used to bring them onto the platform.
              </div>
            </div>
            {memberInviteError ? (
              <div style={{ color: '#fca5a5', fontSize: 12, fontWeight: 700 }}>{memberInviteError}</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <SmallButton type="button" onClick={closeAddMemberModal} disabled={memberInviteSaving}>Close</SmallButton>
              <SmallButton type="button" $accent onClick={() => void submitAddMemberInvite()} disabled={memberInviteSaving}>
                {memberInviteSaving ? 'Sending...' : 'Add Member'}
              </SmallButton>
            </div>
          </ModalCard>
        </ModalOverlay>
      ) : null}
      {meetingModal ? (
        <MeetingModalOverlay onMouseDown={closeMeetingModal}>
          <MeetingModalCard onMouseDown={(event) => event.stopPropagation()}>
            <div style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              <div style={{ display: 'grid', gap: 4 }}>
                <div style={{ margin: 0, color: '#f3f4f6', fontSize: 18, fontWeight: 900 }}>
                  {meetingModal.mode === 'create' ? 'Schedule meeting' : 'Meeting details & notes'}
                </div>
                <div style={{ color: '#9aa4b2', fontSize: 12, lineHeight: 1.5, fontWeight: 700 }}>
                  {meetingModal.orgName} - {meetingModal.mode === 'create'
                    ? 'Schedule a real client meeting tied directly to the authoritative calendar system.'
                    : 'Review the scheduled meeting and capture structured notes for teams and future AI context.'}
                </div>
              </div>
              <SmallButton type="button" onClick={closeMeetingModal}><X size={14} /></SmallButton>
            </div>
            <MeetingModalBody ref={meetingModalBodyRef}>
              {meetingModal.mode === 'create' ? (
                <>
                  <MeetingGrid $cols="repeat(3, minmax(0,1fr))">
                    <MeetingField>
                      <MeetingFieldLabel>Meeting type</MeetingFieldLabel>
                      <MeetingSelect value={meetingDraft.meetingType} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, meetingType: event.target.value as ScheduleMeetingType }))}>
                        {organizationMeetingTypeOptions.map((value) => (
                          <option key={value} value={value}>{SCHEDULE_MEETING_TYPE_LABELS[value]}</option>
                        ))}
                      </MeetingSelect>
                    </MeetingField>
                    <MeetingField>
                      <MeetingFieldLabel>Service contract</MeetingFieldLabel>
                      <MeetingSelect value={meetingDraft.serviceContractId} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, serviceContractId: event.target.value }))} disabled={!meetingServiceOptions.length}>
                        <option value="">{meetingServiceOptions.length ? 'Select contract' : 'No active contracts'}</option>
                        {meetingServiceOptions.map((entry) => (
                          <option key={entry.id} value={entry.id}>{entry.label}{entry.helper ? ` - ${entry.helper}` : ''}</option>
                        ))}
                      </MeetingSelect>
                    </MeetingField>
                    <MeetingField>
                      <MeetingFieldLabel>Internal host</MeetingFieldLabel>
                      <MeetingSelect value={meetingDraft.hostUserUid} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, hostUserUid: event.target.value }))}>
                        <option value="">Select host</option>
                        {internalScheduleUsers.map((entry) => (
                          <option key={entry.userUid} value={entry.userUid}>{entry.name} - {entry.role}</option>
                        ))}
                      </MeetingSelect>
                    </MeetingField>
                  </MeetingGrid>
                  <MeetingGrid $cols="minmax(0,1.45fr) minmax(0,1fr) minmax(0,.8fr)">
                    <MeetingField>
                      <MeetingFieldLabel>Meeting date & time</MeetingFieldLabel>
                      <MeetingInput type="datetime-local" value={meetingDraft.startsAtLocal} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, startsAtLocal: event.target.value }))} />
                    </MeetingField>
                    <MeetingField>
                      <MeetingFieldLabel>Duration</MeetingFieldLabel>
                      <MeetingSelect value={String(meetingDraft.durationMinutes)} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, durationMinutes: Number(event.target.value) || 30 }))}>
                        {[15, 30, 45, 60, 90].map((value) => <option key={value} value={value}>{value} minutes</option>)}
                      </MeetingSelect>
                    </MeetingField>
                    <MeetingField as="div">
                      <MeetingFieldLabel>Add meeting link</MeetingFieldLabel>
                      {meetingDraft.createDiscordMeeting ? (
                        <MeetingDiscordSummaryRow>
                          <MeetingDiscordSummaryAnim>
                            <Lottie animationData={discordAnimation} loop autoplay />
                          </MeetingDiscordSummaryAnim>
                          <div style={{ minWidth: 0, display: 'grid', gap: 2 }}>
                            <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 900, lineHeight: 1.35 }}>
                              Discord meeting
                            </div>
                            
                          </div>
                          <MeetingDiscordRemoveButton
                            type="button"
                            onClick={() =>
                              setMeetingDraft((prev) => ({
                                ...prev,
                                createDiscordMeeting: false,
                              }))
                            }
                            aria-label="Remove Discord meeting"
                            title="Remove Discord meeting"
                          >
                            <X size={14} />
                          </MeetingDiscordRemoveButton>
                        </MeetingDiscordSummaryRow>
                      ) : (
                        <MeetingEditorRowActions style={{ justifyContent: 'flex-start' }}>
                          <MeetingInlineButton
                            type="button"
                            onClick={() =>
                              setMeetingDraft((prev) => ({
                                ...prev,
                                meetingLinks: [...prev.meetingLinks, ''],
                              }))
                            }
                            aria-label="Add meeting link"
                            title="Add meeting link"
                          >
                            <Plus size={14} />
                          </MeetingInlineButton>
                          <MeetingDiscordActionButton
                            type="button"
                            onClick={() =>
                              setMeetingDraft((prev) => ({
                                ...prev,
                                createDiscordMeeting: true,
                                meetingLinks: [],
                              }))
                            }
                            aria-label="Add Discord meeting"
                            title="Add Discord meeting"
                          >
                            <MeetingDiscordButtonAnim>
                              <Lottie animationData={discordAnimation} loop autoplay />
                            </MeetingDiscordButtonAnim>
                          </MeetingDiscordActionButton>
                        </MeetingEditorRowActions>
                      )}
                    </MeetingField>
                  </MeetingGrid>
                  {meetingDraft.meetingLinks.length ? (
                    <MeetingExternalLinkList>
                      {meetingDraft.meetingLinks.map((link, index) => (
                        <MeetingExternalLinkRow key={`create-meeting-link-${index}`}>
                          <MeetingInput
                            value={link}
                            onChange={(event) =>
                              setMeetingDraft((prev) => ({
                                ...prev,
                                meetingLinks: prev.meetingLinks.map((entry, entryIndex) =>
                                  entryIndex === index ? event.target.value : entry,
                                ),
                              }))
                            }
                            placeholder="https://zoom.us/j/... or meet.google.com/..."
                          />
                          <MeetingInlineButton
                            type="button"
                            onClick={() =>
                              setMeetingDraft((prev) => ({
                                ...prev,
                                meetingLinks: prev.meetingLinks.filter((_, entryIndex) => entryIndex !== index),
                              }))
                            }
                            aria-label="Remove meeting link"
                            title="Remove meeting link"
                          >
                            <X size={12} />
                          </MeetingInlineButton>
                        </MeetingExternalLinkRow>
                      ))}
                    </MeetingExternalLinkList>
                  ) : null}
                  <MeetingField>
                    <MeetingFieldLabel>Meeting title</MeetingFieldLabel>
                    <MeetingInput value={meetingDraft.title} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Optional meeting title" />
                  </MeetingField>
                  <MeetingField>
                    <MeetingFieldLabel>Agenda / description</MeetingFieldLabel>
                    <MeetingTextArea value={meetingDraft.description} onChange={(event) => setMeetingDraft((prev) => ({ ...prev, description: event.target.value }))} placeholder="Optional agenda, client context, or meeting objective." />
                  </MeetingField>
                  <MeetingFlatRule />
                  <MeetingGrid>
                    <MeetingFlatSection>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <MeetingFieldLabel>Client organization attendees</MeetingFieldLabel>
                        <MeetingInlineButton type="button" onClick={() => { setInternalAttendeePickerOpen(false); setOrgAttendeePickerOpen((prev) => !prev); }} disabled={availableOrganizationAttendees.length === 0}>
                          <Plus size={14} />
                        </MeetingInlineButton>
                      </div>
                      {selectedOrganizationAttendees.length ? (
                        <AttendeeList>
                          {selectedOrganizationAttendees.map((member) => (
                            <AttendeeRow key={member.userId}>
                              <PortalAvatar size={36} uid={member.uid || member.userId} email={member.email} photoURL={member.photoURL} name={member.displayName} disableNavigation implicitSelfFallback={false} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{member.displayName}</div>
                                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{toUserFacingLabel(member.email)} - {member.isPrimaryAccountHolder ? 'Primary account holder' : member.role}</div>
                              </div>
                              <MeetingInlineButton type="button" onClick={() => setMeetingDraft((prev) => ({ ...prev, orgMemberUserIds: prev.orgMemberUserIds.filter((value) => value !== member.userId) }))}><X size={12} /></MeetingInlineButton>
                            </AttendeeRow>
                          ))}
                        </AttendeeList>
                      ) : (
                        <MeetingHelper>No organization attendees added yet.</MeetingHelper>
                      )}
                      {orgAttendeePickerOpen ? (
                        <AttendeePicker>
                          {availableOrganizationAttendees.length ? availableOrganizationAttendees.map((member) => (
                            <AttendeePickerItem key={member.userId} type="button" onClick={() => { setMeetingDraft((prev) => ({ ...prev, orgMemberUserIds: prev.orgMemberUserIds.includes(member.userId) ? prev.orgMemberUserIds : [...prev.orgMemberUserIds, member.userId] })); setOrgAttendeePickerOpen(false); }}>
                              <PortalAvatar size={34} uid={member.uid || member.userId} email={member.email} photoURL={member.photoURL} name={member.displayName} disableNavigation implicitSelfFallback={false} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{member.displayName}</div>
                                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{toUserFacingLabel(member.email)} - {member.isPrimaryAccountHolder ? 'Primary account holder' : member.role}</div>
                              </div>
                            </AttendeePickerItem>
                          )) : <MeetingHelper>No additional organization members available.</MeetingHelper>}
                        </AttendeePicker>
                      ) : null}
                    </MeetingFlatSection>
                    <MeetingFlatSection>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <MeetingFieldLabel>Additional internal attendees</MeetingFieldLabel>
                        <MeetingInlineButton type="button" onClick={() => { setOrgAttendeePickerOpen(false); setInternalAttendeePickerOpen((prev) => !prev); }} disabled={internalScheduleUsersLoading || availableInternalAttendees.length === 0}>
                          <Plus size={14} />
                        </MeetingInlineButton>
                      </div>
                      {internalScheduleUsersLoading ? <MeetingHelper>Loading internal attendees...</MeetingHelper> : null}
                      {!internalScheduleUsersLoading && selectedInternalAttendees.length ? (
                        <AttendeeList>
                          {selectedInternalAttendees.map((entry) => (
                            <AttendeeRow key={entry.userUid}>
                              <PortalAvatar size={36} uid={entry.userUid} email={entry.email} photoURL={entry.photoURL} name={entry.name} disableNavigation implicitSelfFallback={false} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{entry.name}</div>
                                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{toUserFacingLabel(entry.email)} - {entry.role}</div>
                              </div>
                              <MeetingInlineButton type="button" onClick={() => setMeetingDraft((prev) => ({ ...prev, internalAttendeeUids: prev.internalAttendeeUids.filter((value) => value !== entry.userUid) }))}><X size={12} /></MeetingInlineButton>
                            </AttendeeRow>
                          ))}
                        </AttendeeList>
                      ) : null}
                      {!internalScheduleUsersLoading && !selectedInternalAttendees.length ? <MeetingHelper>No additional internal attendees added yet.</MeetingHelper> : null}
                      {internalAttendeePickerOpen ? (
                        <AttendeePicker>
                          {availableInternalAttendees.length ? availableInternalAttendees.map((entry) => (
                            <AttendeePickerItem key={entry.userUid} type="button" onClick={() => { setMeetingDraft((prev) => ({ ...prev, internalAttendeeUids: prev.internalAttendeeUids.includes(entry.userUid) ? prev.internalAttendeeUids : [...prev.internalAttendeeUids, entry.userUid] })); setInternalAttendeePickerOpen(false); }}>
                              <PortalAvatar size={34} uid={entry.userUid} email={entry.email} photoURL={entry.photoURL} name={entry.name} disableNavigation implicitSelfFallback={false} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{entry.name}</div>
                                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>{toUserFacingLabel(entry.email)} - {entry.role}</div>
                              </div>
                            </AttendeePickerItem>
                          )) : <MeetingHelper>No additional internal attendees available.</MeetingHelper>}
                        </AttendeePicker>
                      ) : null}
                    </MeetingFlatSection>
                  </MeetingGrid>
                </>
              ) : activeMeetingRecord ? (
                <>
                  {!isPendingMeetingRequest ? (
                    <MeetingGrid>
                      <MeetingFlatSection>
                        <div style={labelStyle}>Meeting Type</div>
                        <div style={{ color: '#f3f4f6', fontSize: 16, fontWeight: 900 }}>
                          {SCHEDULE_MEETING_TYPE_LABELS[activeMeetingRecord.meetingType]}
                        </div>
                      </MeetingFlatSection>
                      <MeetingFlatSection>
                        <div style={labelStyle}>Service Contract</div>
                        <div style={{ color: '#f3f4f6', fontSize: 16, fontWeight: 900 }}>
                          {formatMeetingServiceLabel(
                            serviceContractsById.get(activeMeetingRecord.serviceContractId ?? '')?.serviceName
                            || activeMeetingRecord.serviceName
                            || activeMeetingRecord.serviceCode
                            || null,
                          )}
                        </div>
                      </MeetingFlatSection>
                      <MeetingFlatSection>
                        <div style={labelStyle}>Date & Time</div>
                        <div style={{ color: '#f3f4f6', fontSize: 16, fontWeight: 900 }}>
                          {formatMeetingDateRange(activeMeetingRecord.startsAt, activeMeetingRecord.endsAt)}
                        </div>
                        <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700 }}>
                          {activeMeetingRecord.timezone || '--'}
                        </div>
                      </MeetingFlatSection>
                    </MeetingGrid>
                  ) : null}
                  {isPendingMeetingRequest ? (
                    <MeetingEditorSection>
                      <MeetingEditorRow>
                        <MeetingEditorRowHeader>
                          <MeetingFieldLabel>Confirm meeting request</MeetingFieldLabel>
                        </MeetingEditorRowHeader>
                        <MeetingHelper>
                          Assign the internal host, set the final meeting type and time, and save the actual meeting details before confirming this request for {formatRequestedMeetingDate(activeMeetingRecord.requestedForDate)}.
                        </MeetingHelper>
                      </MeetingEditorRow>
                      <MeetingGrid $cols="repeat(3, minmax(0,1fr))">
                        <MeetingField>
                          <MeetingFieldLabel>Internal host</MeetingFieldLabel>
                          <MeetingSelect value={meetingEventDraft.hostUserUid} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, hostUserUid: event.target.value }))}>
                            <option value="">Select host</option>
                            {internalScheduleUsers.map((entry) => (
                              <option key={entry.userUid} value={entry.userUid}>{entry.name} - {entry.role}</option>
                            ))}
                          </MeetingSelect>
                        </MeetingField>
                        <MeetingField>
                          <MeetingFieldLabel>Meeting type</MeetingFieldLabel>
                          <MeetingSelect value={meetingEventDraft.meetingType} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, meetingType: event.target.value as ScheduleMeetingType }))}>
                            {organizationMeetingTypeOptions.map((value) => (
                              <option key={value} value={value}>{SCHEDULE_MEETING_TYPE_LABELS[value]}</option>
                            ))}
                          </MeetingSelect>
                        </MeetingField>
                        <MeetingField>
                          <MeetingFieldLabel>Service contract</MeetingFieldLabel>
                          <MeetingSelect value={meetingEventDraft.serviceContractId} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, serviceContractId: event.target.value }))} disabled={!meetingServiceOptions.length}>
                            <option value="">{meetingServiceOptions.length ? 'Select contract' : 'No active contracts'}</option>
                            {meetingServiceOptions.map((entry) => (
                              <option key={entry.id} value={entry.id}>{entry.label}{entry.helper ? ` - ${entry.helper}` : ''}</option>
                            ))}
                          </MeetingSelect>
                        </MeetingField>
                      </MeetingGrid>
                      <MeetingGrid $cols="minmax(0,1.4fr) minmax(0,.8fr)">
                        <MeetingField>
                          <MeetingFieldLabel>Meeting date & time</MeetingFieldLabel>
                          <MeetingInput type="datetime-local" value={meetingEventDraft.startsAtLocal} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, startsAtLocal: event.target.value }))} />
                        </MeetingField>
                        <MeetingField>
                          <MeetingFieldLabel>Duration</MeetingFieldLabel>
                          <MeetingSelect value={String(meetingEventDraft.durationMinutes)} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, durationMinutes: Number(event.target.value) || 30 }))}>
                            {[15, 30, 45, 60, 90].map((value) => <option key={value} value={value}>{value} minutes</option>)}
                          </MeetingSelect>
                        </MeetingField>
                      </MeetingGrid>
                      <MeetingField>
                        <MeetingFieldLabel>Meeting title</MeetingFieldLabel>
                        <MeetingInput value={meetingEventDraft.title} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Optional meeting title" />
                      </MeetingField>
                      <MeetingField>
                        <MeetingFieldLabel>Agenda / description</MeetingFieldLabel>
                        <MeetingTextArea value={meetingEventDraft.description} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, description: event.target.value }))} placeholder="Add agenda, objectives, or meeting context." />
                      </MeetingField>
                      <MeetingField as="div">
                        <MeetingFieldLabel>Add meeting link</MeetingFieldLabel>
                        {meetingEventDraft.createDiscordMeeting ? (
                          <MeetingDiscordSummaryRow>
                            <MeetingDiscordSummaryAnim>
                              <Lottie animationData={discordAnimation} loop autoplay />
                            </MeetingDiscordSummaryAnim>
                            <div style={{ minWidth: 0, display: 'grid', gap: 2 }}>
                              <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 900, lineHeight: 1.35 }}>
                                Discord meeting
                              </div>
                            </div>
                            <MeetingDiscordRemoveButton
                              type="button"
                              onClick={() =>
                                setMeetingEventDraft((prev) => ({
                                  ...prev,
                                  createDiscordMeeting: false,
                                }))
                              }
                              aria-label="Remove Discord meeting"
                              title="Remove Discord meeting"
                            >
                              <X size={14} />
                            </MeetingDiscordRemoveButton>
                          </MeetingDiscordSummaryRow>
                        ) : (
                          <MeetingEditorRowActions style={{ justifyContent: 'flex-start' }}>
                            <MeetingInlineButton
                              type="button"
                              onClick={() =>
                                setMeetingEventDraft((prev) => ({
                                  ...prev,
                                  meetingLinks: [...prev.meetingLinks, ''],
                                }))
                              }
                              aria-label="Add meeting link"
                              title="Add meeting link"
                            >
                              <Plus size={14} />
                            </MeetingInlineButton>
                            <MeetingDiscordActionButton
                              type="button"
                              onClick={() =>
                                setMeetingEventDraft((prev) => ({
                                  ...prev,
                                  createDiscordMeeting: true,
                                  meetingLinks: [],
                                }))
                              }
                              aria-label="Add Discord meeting"
                              title="Add Discord meeting"
                            >
                              <MeetingDiscordButtonAnim>
                                <Lottie animationData={discordAnimation} loop autoplay />
                              </MeetingDiscordButtonAnim>
                            </MeetingDiscordActionButton>
                          </MeetingEditorRowActions>
                        )}
                        {!meetingEventDraft.createDiscordMeeting ? (
                          <MeetingExternalLinkList>
                            {meetingEventDraft.meetingLinks.map((link, index) => (
                              <MeetingExternalLinkRow key={`confirm-meeting-link-${index}`}>
                                <MeetingInput
                                  value={link}
                                  onChange={(event) =>
                                    setMeetingEventDraft((prev) => ({
                                      ...prev,
                                      meetingLinks: prev.meetingLinks.map((entry, entryIndex) =>
                                        entryIndex === index ? event.target.value : entry,
                                      ),
                                    }))
                                  }
                                  placeholder="https://zoom.us/j/... or meet.google.com/..."
                                />
                                <MeetingInlineButton
                                  type="button"
                                  onClick={() =>
                                    setMeetingEventDraft((prev) => {
                                      const next = prev.meetingLinks.filter((_, entryIndex) => entryIndex !== index);
                                      return {
                                        ...prev,
                                        meetingLinks: next.length ? next : [''],
                                      };
                                    })
                                  }
                                  disabled={meetingEventDraft.meetingLinks.length === 1 && !asText(link)}
                                >
                                  <X size={12} />
                                </MeetingInlineButton>
                              </MeetingExternalLinkRow>
                            ))}
                          </MeetingExternalLinkList>
                        ) : null}
                      </MeetingField>
                      {meetingEventError ? <MeetingHelper $error>{meetingEventError}</MeetingHelper> : null}
                    </MeetingEditorSection>
                  ) : null}
                  {!isPendingMeetingRequest ? (
                  <MeetingEditorSection>
                    <MeetingEditorRow>
                      <MeetingEditorRowHeader>
                        <MeetingFieldLabel>Editable meeting details</MeetingFieldLabel>
                      </MeetingEditorRowHeader>
                      <MeetingHelper>Update the linked service contract, meeting title, agenda, and external meeting links here.</MeetingHelper>
                    </MeetingEditorRow>
                    <MeetingEditorRow>
                      <MeetingFieldLabel>Service contract</MeetingFieldLabel>
                      <MeetingSelect value={meetingEventDraft.serviceContractId} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, serviceContractId: event.target.value }))} disabled={!meetingServiceOptions.length}>
                        <option value="">{meetingServiceOptions.length ? 'Select contract' : 'No active contracts'}</option>
                        {meetingServiceOptions.map((entry) => (
                          <option key={entry.id} value={entry.id}>{entry.label}{entry.helper ? ` - ${entry.helper}` : ''}</option>
                        ))}
                      </MeetingSelect>
                    </MeetingEditorRow>
                    <MeetingEditorRow>
                      <MeetingFieldLabel>Meeting title</MeetingFieldLabel>
                      <MeetingEditorInput value={meetingEventDraft.title} onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Optional meeting title" />
                    </MeetingEditorRow>
                    <MeetingEditorRow>
                      <MeetingFieldLabel>Agenda / description</MeetingFieldLabel>
                      <MeetingEditorTextArea
                        value={meetingEventDraft.description}
                        onChange={(event) => setMeetingEventDraft((prev) => ({ ...prev, description: event.target.value }))}
                        placeholder="Add agenda, objectives, or meeting context."
                      />
                    </MeetingEditorRow>
                    <MeetingEditorRow>
                      <MeetingEditorRowHeader>
                        <MeetingFieldLabel>Meeting links</MeetingFieldLabel>
                        <MeetingEditorRowActions>
                          <MeetingInlineButton
                            type="button"
                            onClick={() =>
                              setMeetingEventDraft((prev) => ({
                                ...prev,
                                meetingLinks: [...prev.meetingLinks, ''],
                              }))
                            }
                          >
                            <Plus size={14} />
                            Add link
                          </MeetingInlineButton>
                          {canCreateDiscordMeeting && !activeMeetingRecord.discordMeeting ? (
                            <MeetingDiscordActionButton
                              type="button"
                              onClick={() => {
                                void createDiscordMeetingForActiveEvent();
                              }}
                              disabled={meetingDiscordSyncing || meetingEventSaving}
                              aria-label="Add Discord meeting"
                              title="Add Discord meeting"
                            >
                              <MeetingDiscordButtonAnim>
                                <Lottie animationData={discordAnimation} loop autoplay />
                              </MeetingDiscordButtonAnim>
                            </MeetingDiscordActionButton>
                          ) : null}
                        </MeetingEditorRowActions>
                      </MeetingEditorRowHeader>
                      {activeMeetingRecord.discordMeeting ? (
                        <MeetingDiscordSummaryRow>
                          <MeetingDiscordSummaryAnim>
                            <Lottie animationData={discordAnimation} loop autoplay />
                          </MeetingDiscordSummaryAnim>
                          <MeetingDiscordSummaryLink
                            href={activeMeetingRecord.discordMeeting.scheduledEventUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 900, lineHeight: 1.35 }}>
                              Discord meeting
                            </div>
                            <div style={{ color: '#cfd7ea', fontSize: 11, fontWeight: 700, lineHeight: 1.45 }}>
                              {activeMeetingRecord.discordMeeting.voiceChannelName}
                            </div>
                          </MeetingDiscordSummaryLink>
                          <MeetingDiscordRemoveButton
                            type="button"
                            onClick={() => void removeDiscordMeetingForActiveEvent()}
                            disabled={meetingDiscordRemoving}
                            aria-label="Remove Discord meeting"
                            title="Remove Discord meeting"
                          >
                            <X size={14} />
                          </MeetingDiscordRemoveButton>
                        </MeetingDiscordSummaryRow>
                      ) : null}
                      <MeetingExternalLinkList>
                        {meetingEventDraft.meetingLinks.map((link, index) => (
                          <MeetingExternalLinkRow key={`meeting-link-${index}`}>
                            <MeetingInput
                              value={link}
                              onChange={(event) =>
                                setMeetingEventDraft((prev) => ({
                                  ...prev,
                                  meetingLinks: prev.meetingLinks.map((entry, entryIndex) =>
                                    entryIndex === index ? event.target.value : entry,
                                  ),
                                }))
                              }
                              placeholder="https://zoom.us/j/... or meet.google.com/..."
                            />
                            <MeetingInlineButton
                              type="button"
                              onClick={() =>
                                setMeetingEventDraft((prev) => {
                                  const next = prev.meetingLinks.filter((_, entryIndex) => entryIndex !== index);
                                  return {
                                    ...prev,
                                    meetingLinks: next.length ? next : [''],
                                  };
                                })
                              }
                              disabled={meetingEventDraft.meetingLinks.length === 1 && !asText(link)}
                            >
                              <X size={12} />
                            </MeetingInlineButton>
                          </MeetingExternalLinkRow>
                        ))}
                      </MeetingExternalLinkList>
                      {meetingEventDraft.meetingLinks.some((value) => asText(value)) ? (
                        <MeetingExternalLinkPreviewList>
                          {meetingEventDraft.meetingLinks
                            .map((value) => asText(value))
                            .filter(Boolean)
                            .map((value) => (
                              <MeetingExternalLink
                                key={value}
                                href={toMeetingLinkHref(value)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {value}
                              </MeetingExternalLink>
                            ))}
                        </MeetingExternalLinkPreviewList>
                      ) : null}
                      {meetingDiscordError ? <MeetingHelper $error>{meetingDiscordError}</MeetingHelper> : null}
                    </MeetingEditorRow>
                    <div>
                      {meetingEventError ? <MeetingHelper $error>{meetingEventError}</MeetingHelper> : <MeetingHelper>Keep the linked service contract, meeting title, agenda, and external call links accurate before saving changes.</MeetingHelper>}
                    </div>
                  </MeetingEditorSection>
                  ) : null}
                  <MeetingSection>
                    <div style={labelStyle}>Attendees</div>
                    {activeMeetingRecord.attendees.length ? (
                      <AttendeeList>
                        {activeMeetingRecord.attendees.map((attendee) => (
                          <AttendeeRow key={attendee.id}>
                            <PortalAvatar
                              size={36}
                              uid={attendee.userId || attendee.id}
                              email={attendee.email}
                              name={asText(attendee.name) || asText(attendee.email) || 'Attendee'}
                              disableNavigation
                              implicitSelfFallback={false}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900, lineHeight: 1.35 }}>
                                {toUserFacingLabel(asText(attendee.name) || asText(attendee.email))}
                              </div>
                              <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 700, lineHeight: 1.5 }}>
                                {toUserFacingLabel(attendee.email)}{attendee.role ? ` - ${attendee.role}` : ''}
                              </div>
                            </div>
                            <span />
                          </AttendeeRow>
                        ))}
                      </AttendeeList>
                    ) : (
                      <MeetingHelper>No attendees are attached to this meeting yet.</MeetingHelper>
                    )}
                  </MeetingSection>
                  {!isPendingMeetingRequest ? (
                  <MeetingSection style={{ background: 'transparent', border: 'none', padding: 0, boxShadow: 'none' }}>
                    <MeetingNotesSection>
                    <MeetingGrid $cols="1fr">
                      {isOnboardingKickoffMeeting ? (
                        <MeetingField>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <MeetingFieldLabel>Onboarding kickoff profile</MeetingFieldLabel>
                            <MeetingHelper>This kickoff profile becomes foundational context for the PM agent when building the client's initial projects and early milestones.</MeetingHelper>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gap: 8,
                            }}
                          >
                            <div
                              style={{
                                color: assessOnboardingKickoffReadiness(meetingNoteDraft.onboardingProfile).tone,
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: '.18px',
                                textTransform: 'uppercase',
                              }}
                            >
                              Discovery intake readiness: {assessOnboardingKickoffReadiness(meetingNoteDraft.onboardingProfile).status}
                            </div>
                            <MeetingHelper>{assessOnboardingKickoffReadiness(meetingNoteDraft.onboardingProfile).summary}</MeetingHelper>
                            {assessOnboardingKickoffReadiness(meetingNoteDraft.onboardingProfile).missingSignals.length ? (
                              <MeetingHelper>
                                Missing core signals: {assessOnboardingKickoffReadiness(meetingNoteDraft.onboardingProfile).missingSignals.join(' | ')}
                              </MeetingHelper>
                            ) : null}
                          </div>
                          <div style={{ display: 'grid', gap: 18 }}>
                            {ONBOARDING_PROFILE_GROUP_ORDER.map((group) => {
                              const fields = ONBOARDING_PROFILE_FIELDS.filter((field) => field.group === group);
                              if (!fields.length) return null;
                              return (
                                <div key={group} style={{ display: 'grid', gap: 10 }}>
                                  <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 900, letterSpacing: '.18px', textTransform: 'uppercase' }}>
                                    {group}
                                  </div>
                                  <MeetingGrid $cols="repeat(2, minmax(0,1fr))">
                                    {fields.map((field) => (
                              <MeetingField key={field.key}>
                                <MeetingFieldLabel>{field.label}</MeetingFieldLabel>
                                {field.control === 'date' ? (
                                  <MeetingInput
                                    type="date"
                                    value={meetingNoteDraft.onboardingProfile[field.key]}
                                    onChange={(event) => {
                                      setMeetingInternalContextError(null);
                                      setMeetingNoteDraft((prev) => ({
                                        ...prev,
                                        onboardingProfile: {
                                          ...prev.onboardingProfile,
                                          [field.key]: event.target.value,
                                        },
                                      }));
                                    }}
                                  />
                                ) : field.control === 'input' ? (
                                  <MeetingInput
                                    value={meetingNoteDraft.onboardingProfile[field.key]}
                                    onChange={(event) => {
                                      setMeetingInternalContextError(null);
                                      setMeetingNoteDraft((prev) => ({
                                        ...prev,
                                        onboardingProfile: {
                                          ...prev.onboardingProfile,
                                          [field.key]: event.target.value,
                                        },
                                      }));
                                    }}
                                    placeholder={field.placeholder}
                                  />
                                ) : (
                                  <MeetingNotesTextArea
                                    value={meetingNoteDraft.onboardingProfile[field.key]}
                                    onChange={(event) => {
                                      setMeetingInternalContextError(null);
                                      setMeetingNoteDraft((prev) => ({
                                        ...prev,
                                        onboardingProfile: {
                                          ...prev.onboardingProfile,
                                          [field.key]: event.target.value,
                                        },
                                      }));
                                    }}
                                    placeholder={field.placeholder}
                                  />
                                )}
                              </MeetingField>
                                    ))}
                                  </MeetingGrid>
                                </div>
                              );
                            })}
                          </div>
                        </MeetingField>
                      ) : null}
                      <MeetingField>
                        <MeetingFieldLabel>Meeting summary</MeetingFieldLabel>
                        <MeetingNotesTextArea value={meetingNoteDraft.summary} onChange={(event) => { setMeetingInternalContextError(null); setMeetingNoteDraft((prev) => ({ ...prev, summary: event.target.value })); }} placeholder="Capture the main discussion, business context, and the outcome of the meeting." />
                      </MeetingField>
                      <MeetingField>
                        <MeetingFieldLabel>Client updates / asks</MeetingFieldLabel>
                        <MeetingNotesTextArea value={meetingNoteDraft.clientUpdates} onChange={(event) => { setMeetingInternalContextError(null); setMeetingNoteDraft((prev) => ({ ...prev, clientUpdates: event.target.value })); }} placeholder="Document requests, concerns, priorities, blockers, or anything the client surfaced." />
                      </MeetingField>
                      <MeetingField>
                        <MeetingFieldLabel>Decisions / commitments</MeetingFieldLabel>
                        <MeetingNotesTextArea value={meetingNoteDraft.decisions} onChange={(event) => { setMeetingInternalContextError(null); setMeetingNoteDraft((prev) => ({ ...prev, decisions: event.target.value })); }} placeholder="Record decisions made, commitments given, and any approvals that were discussed." />
                      </MeetingField>
                      <MeetingField>
                        <MeetingFieldLabel>Next steps / follow-ups</MeetingFieldLabel>
                        <MeetingNotesTextArea value={meetingNoteDraft.nextSteps} onChange={(event) => { setMeetingInternalContextError(null); setMeetingNoteDraft((prev) => ({ ...prev, nextSteps: event.target.value })); }} placeholder="List follow-up actions, ownership, and what should happen next." />
                      </MeetingField>
                      <MeetingField>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <MeetingFieldLabel>Internal context for teams / AI</MeetingFieldLabel>
                          <FooterButton type="button" onClick={() => void generateMeetingInternalContext()} disabled={meetingNoteLoading || meetingInternalContextGenerating || !canGenerateMeetingInternalContext}>
                            {meetingInternalContextGenerating ? 'Generating...' : asText(meetingNoteDraft.internalContext) ? 'Regenerate with AI' : 'Generate with AI'}
                          </FooterButton>
                        </div>
                        {asText(meetingNoteDraft.internalContext) ? <MeetingNotesTextArea value={meetingNoteDraft.internalContext} onChange={(event) => { setMeetingNoteDraft((prev) => ({ ...prev, internalContext: event.target.value })); }} placeholder="Generate internal context or add it manually." /> : <MeetingHelper>Generate internal context to create a reusable internal-only summary for teams and AI.</MeetingHelper>}
                        {meetingInternalContextGenerating ? <MeetingHelper>Generating internal context...</MeetingHelper> : null}
                        {meetingInternalContextError ? <MeetingHelper $error>{meetingInternalContextError}</MeetingHelper> : null}
                      </MeetingField>
                    </MeetingGrid>
                    {meetingNoteLoading ? <MeetingHelper>Loading existing meeting notes...</MeetingHelper> : null}
                    {meetingNoteError ? <MeetingHelper $error>{meetingNoteError}</MeetingHelper> : null}
                    {!meetingNoteLoading && !meetingNoteError ? <MeetingHelper>These notes stay linked to the scheduled meeting and become reusable client context for internal teams and future AI agents.</MeetingHelper> : null}
                    </MeetingNotesSection>
                  </MeetingSection>
                  ) : null}
                </>
              ) : (
                <EmptyState>No meeting record found.</EmptyState>
              )}
            </MeetingModalBody>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <div style={{ color: '#9aa4b2', fontSize: 11, lineHeight: 1.5, fontWeight: 700 }}>
                {meetingModal.mode === 'create'
                  ? 'This will create a real scheduled meeting for the selected organization and attendees.'
                  : activeMeetingRecord
                    ? isPendingMeetingRequest
                      ? `Client request submitted for ${formatRequestedMeetingDate(activeMeetingRecord.requestedForDate)}.`
                      : activeMeetingNote?.updatedAt
                      ? `Meeting notes last updated ${formatDateLabel(activeMeetingNote.updatedAt)}.`
                      : `${SCHEDULE_MEETING_TYPE_LABELS[activeMeetingRecord.meetingType]} scheduled for ${formatDateLabel(activeMeetingRecord.startsAt)}.`
                    : 'Review the scheduled meeting.'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {meetingModal.mode === 'create' ? <FooterButton type="button" onClick={closeMeetingModal}>Cancel</FooterButton> : null}
                {meetingModal.mode === 'create' ? (
                  <FooterButton type="button" $accent onClick={() => void saveMeetingDraft()} disabled={meetingSaving}>
                    {meetingSaving ? 'Scheduling...' : 'Schedule meeting'}
                  </FooterButton>
                ) : activeMeetingRecord && isPendingMeetingRequest ? (
                  <FooterButton type="button" $accent onClick={() => void confirmPendingMeetingRequest()} disabled={meetingEventSaving}>
                    {meetingEventSaving ? 'Confirming...' : 'Confirm request'}
                  </FooterButton>
                ) : activeMeetingRecord && (hasUnsavedMeetingEventChanges || hasUnsavedMeetingNoteChanges) ? (
                  <FooterButton type="button" $accent onClick={() => void saveMeetingChanges()} disabled={meetingNoteLoading || meetingNoteSaving || meetingEventSaving}>
                    {meetingNoteSaving || meetingEventSaving ? 'Saving...' : 'Save changes'}
                  </FooterButton>
                ) : activeMeetingRecord ? (
                  <FooterButton type="button" $accent disabled>
                    Save changes
                  </FooterButton>
                ) : null}
              </div>
            </div>
          </MeetingModalCard>
        </MeetingModalOverlay>
      ) : null}
    </Page>
  );
}

